import { useContext, useEffect, useState, useCallback, useRef, useMemo, memo } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import axios from "axios";
import { API_URL } from "../../Constants";
import { AuthContext } from "../context/AuthContext";
import { toast } from "react-toastify";
import { FaPhone } from "react-icons/fa";
import {
  X,
  History,
  PhoneCall,
  Video,
  MoreVertical,
  MessageSquare,
  Mail,
  RefreshCw,
  CalendarPlus,
  CalendarCheck,
  Tag,
  CheckCircle2,
  Ban,
  Search,
  PhoneIncoming,
  PhoneMissed,
  Clock,
  FileText,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

const ItemTypes = { CARD: "card" };

// Remembers companies whose IVR key was already verified this session, so
// every click-to-call after the first one skips the extra GET round trip.
const ivrConfiguredCache = new Set();

async function isIvrConfigured(targetId, token) {
  const key = String(targetId);
  if (ivrConfiguredCache.has(key)) return true;
  try {
    const res = await axios.get(`${API_URL}/api/call-api-key/${targetId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = res.data;
    const ok = !(!d || d === "" || (Array.isArray(d) && d.length === 0));
    if (ok) ivrConfiguredCache.add(key);
    return ok;
  } catch (err) {
    console.error("IVR API Check failed:", err);
    return false;
  }
}

// Builds a Google search URL for an exact-match ("...") query, e.g.
// https://www.google.com/search?q=%229876543210%22
const googleExactSearchUrl = (value) =>
  `https://www.google.com/search?q=${encodeURIComponent(`"${String(value).trim()}"`)}`;

// How many leads to load per status per fetch (first page from /status-wise,
// and per "Load more" click from /status/{statusName}).
const PAGE_SIZE = 50;

/**
 * Decodes the JWT payload without a network call. The token already carries
 * everything needed for click-to-call (mobileNumber, sub/email, accountType),
 * so there's no need to hit /api/companyuser/... just to look up the caller's
 * own number - it's sitting right there in the token you already have.
 *
 * Returns null if there's no token or it can't be parsed, so callers can fall
 * back gracefully instead of throwing.
 */
function getJwtPayload(token) {
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (err) {
    console.error("Token parsing error:", err);
    return null;
  }
}

// Formats an ISO date-time string as dd-mm-yyyy hh:mm AM/PM.
function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDuration(value) {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "string" && value.includes(":")) return value;
  const total = Number(value);
  if (isNaN(total)) return String(value);
  const mins = Math.floor(total / 60);
  const secs = Math.round(total % 60);
  return `${mins}m ${secs}s`;
}

// Call statuses from the API: "Answer", "Missed", "Client Missed",
// "Rejected - DUPLICATE_SESSION". Only "Answer"-type statuses are green;
// everything else (missed / rejected / busy / failed) is red.
const isCallAnswered = (status) => {
  const s = String(status || "").toLowerCase().replace(/[_-]/g, " ").trim();
  if (!s) return false;
  if (/miss|not\s*answer|no\s*answer|unanswer|busy|fail|reject|declin|cancel/.test(s)) return false;
  return /answer|connect|complet|success|picked|receiv/.test(s);
};

const getLastCall = (lead) => {
  const calls = Array.isArray(lead?.callRecordings) ? lead.callRecordings : [];
  if (calls.length === 0) return null;
  let best = calls[calls.length - 1];
  let bestTime = -Infinity;
  calls.forEach((c) => {
    const t = new Date(c.startTime || c.capturedAt).getTime();
    if (!isNaN(t) && t >= bestTime) {
      bestTime = t;
      best = c;
    }
  });
  return best;
};

const canTranscribe = (call) => {
  if (!call?.callSessionId || !call?.resourceURL) return false;
  const d = call.callDuration;
  if (typeof d === "string" && d.includes(":")) return /[1-9]/.test(d);
  return Number(d) > 0;
};

function useCallTranscript(leadId, callSessionId) {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("loading");
  const [data, setData] = useState(null);

  const load = async (forceGenerate) => {
    if (!callSessionId) return;
    setLoading(true);
    setPhase(forceGenerate ? "transcribing" : "loading");
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    const generate = () => {
      setPhase("transcribing");
      const transcriptCompanyId =
        user?.accountType === "COMPANY" ? user?.id : user?.companyId;
      return axios.post(
        `${API_URL}/api/transcription/lead/${leadId}`,
        { companyId: transcriptCompanyId, callSessionId },
        { headers }
      );
    };
    try {
      let res;
      if (forceGenerate) {
        res = await generate();
      } else {
        try {
          res = await axios.get(
            `${API_URL}/api/transcription/session/${encodeURIComponent(callSessionId)}`,
            { headers }
          );
        } catch (err) {
          if (err.response?.status !== 404) throw err;
          res = await generate();
        }
      }
      setData(res.data);
    } catch (err) {
      console.error("Transcript error:", err);
      setData({
        status: "FAILED",
        errorMessage:
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Could not load the transcript.",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggle = (e) => {
    if (e) e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (!data && !loading) load(false);
  };

  const retry = (e) => {
    if (e) e.stopPropagation();
    if (!loading) load(true);
  };

  return { open, loading, phase, data, toggle, retry };
}

function TranscriptView({ transcript, compact }) {
  const { loading, phase, data, retry } = transcript;
  const textSize = compact ? "text-[10px]" : "text-xs";

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${textSize} text-gray-500 dark:text-gray-300`}>
        <div className="w-3 h-3 border-2 border-[#673AB7] border-t-transparent rounded-full animate-spin shrink-0"></div>
        {phase === "transcribing"
          ? "Transcribing the call, this can take a minute..."
          : "Loading transcript..."}
      </div>
    );
  }

  if (!data) return null;

  if (data.status === "FAILED") {
    return (
      <div className={`${textSize} text-red-600 dark:text-red-400 flex items-start justify-between gap-2`}>
        <span className="break-words">{data.errorMessage || "Transcription failed."}</span>
        <button
          onClick={retry}
          className="shrink-0 px-1.5 py-px rounded bg-red-50 text-red-600 font-semibold hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300"
        >
          Retry
        </button>
      </div>
    );
  }

  const lines = data.transcript || [];
  if (lines.length === 0) {
    return <p className={`${textSize} text-gray-400`}>No transcript available.</p>;
  }

  return (
    <div className="space-y-1.5">
      {!compact && data.overallSummary && (
        <p className="text-xs leading-snug rounded-md bg-[#F5F3FF] text-[#4C1D95] dark:bg-purple-900/30 dark:text-purple-100 p-2">
          <span className="font-semibold">Summary: </span>
          {data.overallSummary}
        </p>
      )}
      {lines.map((line, i) => {
        const isExec = line.speaker === "EXECUTIVE";
        return (
          <div key={i} className={`flex ${isExec ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[88%] rounded-lg px-2 py-1 leading-snug ${textSize} ${isExec
                  ? "bg-[#EDE9FE] text-[#4C1D95] dark:bg-purple-900/40 dark:text-purple-100"
                  : "bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-100"
                }`}
            >
              <span className="block text-[9px] font-semibold uppercase tracking-wide opacity-60">
                {isExec ? "Executive" : "Client"}
              </span>
              {line.text}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Call transcription: rating, summary, transcript + extracted lead details ──
// Same flow as Sales God: load existing transcripts for the lead in one call,
// "Transcribe & Rate" generates one on demand, extracted details can be
// reviewed (edit / approve / reject) right under the transcript.
const isZeroDurationClip = (duration) => {
  if (duration === null || duration === undefined || duration === "") return true;
  const asNumber = Number(duration);
  if (!Number.isNaN(asNumber)) return asNumber <= 0;
  // clock-style fallback, e.g. "0:00" / "00:00:00"
  return /^[0:.\s]+$/.test(String(duration));
};

const INTENT_CATEGORIES = [
  { value: "INTERESTED", label: "Interested" },
  { value: "PRICE_INQUIRY", label: "Price inquiry" },
  { value: "INFORMATION_REQUEST", label: "Information request" },
  { value: "CALLBACK_REQUESTED", label: "Callback requested" },
  { value: "SITE_VISIT_OR_DEMO", label: "Site visit / demo" },
  { value: "NOT_INTERESTED", label: "Not interested" },
  { value: "WRONG_NUMBER", label: "Wrong number" },
  { value: "OTHER", label: "Other" },
];

const EXTRACTION_FIELDS = [
  { key: "name", label: "Name", placeholder: "Client name" },
  { key: "email", label: "Email", placeholder: "client@example.com" },
  { key: "phone", label: "Phone (saved as alt. contact)", placeholder: "10-digit mobile number" },
];

const EXTRACTION_STATUS = {
  PENDING_REVIEW: { label: "Pending review", bg: "#f5a623" },
  APPROVED: { label: "Approved", bg: "#16a34a" },
  REJECTED: { label: "Rejected", bg: "#9a9a9a" },
};

const extractionToDraft = (ex) => ({
  name: ex?.reviewed?.name || "",
  email: ex?.reviewed?.email || "",
  phone: ex?.reviewed?.phone || "",
  intent: ex?.reviewed?.intent || "",
  intentCategory: ex?.reviewed?.intentCategory || "",
});

const buildLeadPatchFromExtraction = (lead, fields) => {
  const patch = {};
  if (fields.name) patch.name = fields.name;
  if (fields.email) patch.email = fields.email;
  const leadDigits = String(lead?.phone || "").replace(/\D/g, "");
  if (fields.phone && !leadDigits.endsWith(fields.phone)) patch.otherContactNo = fields.phone;
  if (fields.intent || fields.intentCategory) {
    patch.customFields = {
      ...(lead?.customFields || {}),
      ...(fields.intent ? { callIntent: fields.intent } : {}),
      ...(fields.intentCategory ? { callIntentCategory: fields.intentCategory } : {}),
    };
  }
  return patch;
};

function StarRating({ value = 0 }) {
  return (
    <span className="tracking-[2px] text-base leading-none">
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= value ? "text-[#f5a623]" : "text-gray-300 dark:text-gray-600"}>★</span>
      ))}
    </span>
  );
}

const extractionInputCls =
  "w-full border border-gray-300 dark:border-gray-600 rounded p-1.5 text-xs mt-0.5 dark:bg-gray-800 dark:text-white outline-none focus:ring-1 focus:ring-[#8B5CF6] disabled:opacity-60";

const ExtractionPanel = memo(function ExtractionPanel({ callSessionId, extraction, draft, busy, onDraftChange, onAction }) {
  if (!extraction) {
    return (
      <p className="mt-3 text-xs italic text-gray-400">
        No lead details were extracted from this call.
      </p>
    );
  }

  const current = draft || extractionToDraft(extraction);
  const original = extractionToDraft(extraction);
  const pending = extraction.status === "PENDING_REVIEW";
  const dirty = Object.keys(original).some((k) => (original[k] || "") !== (current[k] || ""));
  const statusStyle = EXTRACTION_STATUS[extraction.status] || EXTRACTION_STATUS.PENDING_REVIEW;

  return (
    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Extracted Lead Details</span>
        <span className="flex items-center gap-2">
          {extraction.extractionConfidence && (
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              AI confidence: {extraction.extractionConfidence}
            </span>
          )}
          <span
            className="text-white text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: statusStyle.bg }}
          >
            {statusStyle.label}
          </span>
        </span>
      </div>

      <div className="space-y-2">
        {EXTRACTION_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{f.label}</label>
            <input
              type="text"
              className={extractionInputCls}
              placeholder={f.placeholder}
              value={current[f.key] || ""}
              disabled={!pending || busy}
              onChange={(e) => onDraftChange(callSessionId, f.key, e.target.value)}
            />
            {pending &&
              original[f.key] &&
              extraction.extracted?.[f.key] &&
              extraction.extracted[f.key] !== (current[f.key] || "") && (
                <div className="text-[10px] text-gray-400 mt-0.5">AI heard: {extraction.extracted[f.key]}</div>
              )}
          </div>
        ))}

        <div>
          <label className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Intent category</label>
          <select
            className={extractionInputCls}
            value={current.intentCategory || ""}
            disabled={!pending || busy}
            onChange={(e) => onDraftChange(callSessionId, "intentCategory", e.target.value)}
          >
            <option value="">Not set</option>
            {INTENT_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Call intent</label>
          <textarea
            rows={2}
            className={`${extractionInputCls} resize-none`}
            placeholder="Why the client called"
            value={current.intent || ""}
            disabled={!pending || busy}
            onChange={(e) => onDraftChange(callSessionId, "intent", e.target.value)}
          />
        </div>
      </div>

      {pending ? (
        <div className="flex flex-wrap justify-end gap-2 mt-3">
          <button
            disabled={busy}
            onClick={() => onAction(callSessionId, "reject")}
            className="px-3 py-1.5 text-xs rounded bg-[#F3F4F6] text-[#374151] hover:bg-[#E5E7EB] dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500 disabled:opacity-50"
          >
            Reject
          </button>
          <button
            disabled={busy || !dirty}
            onClick={() => onAction(callSessionId, "save")}
            className="px-3 py-1.5 text-xs rounded bg-[#F3F4F6] text-[#374151] hover:bg-[#E5E7EB] dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500 disabled:opacity-50"
          >
            Save edits
          </button>
          <button
            disabled={busy}
            onClick={() => onAction(callSessionId, "approve")}
            className="px-3 py-1.5 text-xs rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          >
            {busy ? "Working…" : "Approve & save to lead"}
          </button>
        </div>
      ) : (
        extraction.reviewedByName && (
          <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
            {extraction.status === "APPROVED" ? "Approved" : "Rejected"} by {extraction.reviewedByName}
          </div>
        )
      )}
    </div>
  );
});

const CallRecordingItem = memo(function CallRecordingItem({
  call,
  result,
  extraction,
  draft,
  transcribing,
  extractionBusy,
  onTranscribe,
  onDraftChange,
  onExtractionAction,
}) {
  const answered = isCallAnswered(call.status);
  const zero = isZeroDurationClip(call.callDuration);
  const canRun = !!call.callSessionId && !!call.resourceURL && !zero;
  const retry = () => onTranscribe(call.callSessionId);

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-3 bg-white dark:bg-gray-700 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">
            {formatDateTime(call.startTime)}
          </p>
          <p className="text-[10px] text-gray-400 truncate" title={call.callSessionId}>
            Session: {call.callSessionId}
          </p>
        </div>
        <span
          className="text-white text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize shrink-0"
          style={{ backgroundColor: answered ? "#16a34a" : "#F46653" }}
        >
          {call.status}
        </span>
      </div>

      <div className="flex items-center mt-2">
        <span className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
          <Clock className="w-3.5 h-3.5" />
          {formatDuration(call.callDuration)}
        </span>
      </div>

      {call.resourceURL && (
        <audio src={call.resourceURL} controls preload="none" className="w-full h-9 mt-2" />
      )}

      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-600">
        {zero ? (
          <p className="text-xs italic text-gray-400">
            This recording is 0:00 - there's no audio to transcribe, so it's skipped automatically.
          </p>
        ) : transcribing ? (
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-300">
            <div className="w-3 h-3 border-2 border-[#673AB7] border-t-transparent rounded-full animate-spin shrink-0"></div>
            Transcribing the call, this can take a minute...
          </div>
        ) : result ? (
          result.status === "FAILED" ? (
            <TranscriptView transcript={{ loading: false, phase: "loading", data: result, retry }} />
          ) : (
            <div className="space-y-2">
              <div className="flex items-center flex-wrap gap-2 text-xs">
                <span className="font-semibold text-gray-700 dark:text-gray-200">Executive Rating:</span>
                <StarRating value={result.executiveRating} />
                <span className="text-gray-500 dark:text-gray-400">({result.executiveRating ?? 0}/5)</span>
              </div>

              {result.ratingRemarks && (
                <p className="text-xs text-gray-700 dark:text-gray-200">{result.ratingRemarks}</p>
              )}

              {Array.isArray(result.improvementSuggestions) && result.improvementSuggestions.length > 0 && (
                <div>
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                    Suggestions to improve:
                  </span>
                  <ul className="mt-1 pl-4 list-disc space-y-1">
                    {result.improvementSuggestions.map((sug, i) => (
                      <li key={i} className="text-xs text-gray-700 dark:text-gray-200">{sug}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="max-h-60 overflow-y-auto lm-scroll overscroll-contain pr-1">
                <TranscriptView transcript={{ loading: false, phase: "loading", data: result, retry }} />
              </div>

              <ExtractionPanel
                callSessionId={call.callSessionId}
                extraction={extraction}
                draft={draft}
                busy={extractionBusy}
                onDraftChange={onDraftChange}
                onAction={onExtractionAction}
              />
            </div>
          )
        ) : canRun ? (
          <button
            onClick={retry}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-[#F3E8FF] text-[#673AB7] hover:bg-[#E9D5FF] dark:bg-purple-900 dark:text-purple-200 transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            Transcribe &amp; Rate
          </button>
        ) : (
          <p className="text-xs italic text-gray-400">No audio available to transcribe.</p>
        )}
      </div>
    </div>
  );
});

const CallRecordingsList = memo(function CallRecordingsList({ lead, data, loading, onLeadUpdated }) {
  const { user } = useContext(AuthContext);
  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  const [results, setResults] = useState({}); // callSessionId -> transcription result
  const [extractions, setExtractions] = useState({}); // callSessionId -> extraction
  const [drafts, setDrafts] = useState({}); // callSessionId -> editable draft
  const [transcribing, setTranscribing] = useState({}); // callSessionId -> true while running
  const [busyExtractionId, setBusyExtractionId] = useState(null);

  // Latest values for the stable handlers below (avoids re-creating them on
  // every keystroke, which would re-render every recording).
  const leadRef = useRef(lead);
  const extractionsRef = useRef(extractions);
  const draftsRef = useRef(drafts);
  const onLeadUpdatedRef = useRef(onLeadUpdated);
  leadRef.current = lead;
  extractionsRef.current = extractions;
  draftsRef.current = drafts;
  onLeadUpdatedRef.current = onLeadUpdated;

  // One request for every existing transcript + one for extractions, fired in
  // parallel with the recordings fetch the parent is already doing.
  useEffect(() => {
    if (!lead?.leadId) return;
    let cancelled = false;
    (async () => {
      const [existingRes, extractionRes] = await Promise.all([
        axios
          .get(`${API_URL}/api/transcription/lead/${lead.leadId}`, { headers: lmAuthHeader() })
          .catch(() => ({ data: [] })),
        axios
          .get(`${API_URL}/api/lead-extractions/lead/${lead.leadId}`, { headers: lmAuthHeader() })
          .catch(() => ({ data: [] })),
      ]);
      if (cancelled) return;

      const resultMap = {};
      (Array.isArray(existingRes.data) ? existingRes.data : []).forEach((t) => {
        if (t.callSessionId) resultMap[t.callSessionId] = t;
      });
      const extractionMap = {};
      const draftMap = {};
      (Array.isArray(extractionRes.data) ? extractionRes.data : []).forEach((ex) => {
        if (ex.callSessionId) {
          extractionMap[ex.callSessionId] = ex;
          draftMap[ex.callSessionId] = extractionToDraft(ex);
        }
      });
      // Keep anything the user generated while this was loading.
      setResults((prev) => ({ ...resultMap, ...prev }));
      setExtractions((prev) => ({ ...extractionMap, ...prev }));
      setDrafts((prev) => ({ ...draftMap, ...prev }));
    })();
    return () => {
      cancelled = true;
    };
  }, [lead?.leadId]);

  const handleTranscribe = useCallback(
    async (callSessionId) => {
      const l = leadRef.current;
      if (!l?.leadId || !callSessionId) return;
      setTranscribing((prev) => ({ ...prev, [callSessionId]: true }));
      try {
        const res = await axios.post(
          `${API_URL}/api/transcription/lead/${l.leadId}`,
          { companyId, callSessionId },
          { headers: lmAuthHeader() }
        );
        setResults((prev) => ({ ...prev, [callSessionId]: res.data }));
        if (res.data?.status === "SUCCESS") {
          try {
            const exRes = await axios.get(
              `${API_URL}/api/lead-extractions/session/${encodeURIComponent(callSessionId)}`,
              { headers: lmAuthHeader() }
            );
            setExtractions((prev) => ({ ...prev, [callSessionId]: exRes.data }));
            setDrafts((prev) => ({ ...prev, [callSessionId]: extractionToDraft(exRes.data) }));
          } catch (err) {
            if (err?.response?.status !== 404) console.error("Error fetching extracted details", err);
          }
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to transcribe this recording.");
      } finally {
        setTranscribing((prev) => {
          const next = { ...prev };
          delete next[callSessionId];
          return next;
        });
      }
    },
    [companyId]
  );

  const handleDraftChange = useCallback((callSessionId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [callSessionId]: { ...(prev[callSessionId] || {}), [field]: value },
    }));
  }, []);

  const handleExtractionAction = useCallback(async (callSessionId, action) => {
    const ex = extractionsRef.current[callSessionId];
    if (!ex) return;
    const draft = draftsRef.current[callSessionId] || extractionToDraft(ex);
    setBusyExtractionId(ex.id);
    try {
      let res;
      if (action === "save") {
        res = await axios.put(`${API_URL}/api/lead-extractions/${ex.id}`, draft, { headers: lmAuthHeader() });
        toast.success("Changes saved");
      } else if (action === "approve") {
        res = await axios.post(`${API_URL}/api/lead-extractions/${ex.id}/approve`, draft, { headers: lmAuthHeader() });
        toast.success("Approved and saved to lead");
        const l = leadRef.current;
        if (onLeadUpdatedRef.current && l?.leadId) {
          onLeadUpdatedRef.current(l.leadId, buildLeadPatchFromExtraction(l, res.data.reviewed || {}));
        }
      } else {
        res = await axios.post(`${API_URL}/api/lead-extractions/${ex.id}/reject`, null, { headers: lmAuthHeader() });
        toast.success("Extraction rejected");
      }
      setExtractions((prev) => ({ ...prev, [callSessionId]: res.data }));
      setDrafts((prev) => ({ ...prev, [callSessionId]: extractionToDraft(res.data) }));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setBusyExtractionId(null);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-b-4 border-[#673AB7]"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <p className="text-center text-sm text-gray-400 py-6">No call recordings found.</p>;
  }

  return (
    <>
      {data.map((call, idx) => {
        const id = call.callSessionId;
        const ex = id ? extractions[id] : undefined;
        return (
          <CallRecordingItem
            key={id || idx}
            call={call}
            result={id ? results[id] : undefined}
            extraction={ex}
            draft={id ? drafts[id] : undefined}
            transcribing={!!(id && transcribing[id])}
            extractionBusy={!!(ex && busyExtractionId === ex.id)}
            onTranscribe={handleTranscribe}
            onDraftChange={handleDraftChange}
            onExtractionAction={handleExtractionAction}
          />
        );
      })}
    </>
  );
});

// Reads the auth token straight from localStorage - used by the standalone
// Quick Actions modals below (they live outside DragAndDropKanban, so they
// don't have the component's `token` state in closure).
const lmAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const LM_APT_STATUS_OPTIONS = ["SCHEDULED", "COMPLETED", "CANCELLED", "RESCHEDULED", "NO_SHOW"];

// Small inline WhatsApp glyph (same mark used on Sales God) so the Quick
// Actions menu and WhatsApp modal don't need an extra icon dependency.
const LeadsWhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.888-.788-1.487-1.761-1.663-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

/**
 * Inline click-to-edit field, same interaction pattern as Sales God:
 * click to start editing, Enter to save, Escape to cancel, blur to save.
 */
function EditableField({ value, onSave, label, options }) {
  const [isEditing, setIsEditing] = useState(false);
  const [tempVal, setTempVal] = useState(value || "");

  useEffect(() => {
    setTempVal(value || "");
  }, [value]);

  const handleBlurOrSubmit = () => {
    // Check for only spaces
    if (typeof tempVal === "string" && tempVal.length > 0 && tempVal.trim().length === 0) {
      toast.warning(`${label} cannot contain only spaces.`);
      setIsEditing(false);
      setTempVal(value || ""); // reset to original value
      return;
    }

    const trimmedVal = typeof tempVal === "string" ? tempVal.trim() : tempVal;
    setIsEditing(false);

    if (trimmedVal !== (value || "")) {
      onSave(trimmedVal);
    } else {
      setTempVal(value || "");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleBlurOrSubmit();
    if (e.key === "Escape") {
      setIsEditing(false);
      setTempVal(value || "");
    }
  };

  if (isEditing) {
    if (options && options.length > 0) {
      return (
        <select
          autoFocus
          className="w-full border border-[#8B5CF6] rounded p-2 md:p-1 text-base md:text-sm dark:bg-gray-800 dark:text-white outline-none"
          value={tempVal}
          onChange={(e) => setTempVal(e.target.value)}
          onBlur={handleBlurOrSubmit}
          onKeyDown={handleKeyDown}
        >
          <option value="">— Select {label} —</option>
          {options.map((opt, i) => (
            <option key={i} value={opt.name || opt.value || opt}>
              {opt.name || opt.value || opt}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        autoFocus
        type="text"
        className="w-full border border-[#8B5CF6] rounded p-2 md:p-1 text-base md:text-sm dark:bg-gray-800 dark:text-white outline-none"
        value={tempVal}
        onChange={(e) => setTempVal(e.target.value)}
        onBlur={handleBlurOrSubmit}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <span
      onClick={() => setIsEditing(true)}
      className="cursor-pointer break-all inline-flex items-center gap-1 group py-1 md:py-0"
      title={`Click to edit ${label}`}
    >
      <span className={!value ? "italic text-gray-400" : ""}>
        {value || `Add ${label}`}
      </span>
      <span className="text-xs md:text-[10px] opacity-60 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        ✎
      </span>
    </span>
  );
}

function CardInner({ lead, moveLead, accountType, onCardClick, onOpenHistory, onIvrError, onVideoCall, canVideoCall, onOpenActions, onQuickStatus, isHighlighted, cardRefs }) {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CARD,
    // Touch screens can't drag-and-drop between columns; phones use the
    // "Status" button on the card instead (see the phone action bar below).
    canDrag: () => !window.matchMedia("(max-width: 767px)").matches,
    item: {
      id: lead.leadId,
      currentStatus: lead.statusName || "New",
      currentColor: lead.statusColor || "#3b82f6",
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const { user } = useContext(AuthContext);
  const [isCalling, setIsCalling] = useState(false);
  const [isCreatingVideoCall, setIsCreatingVideoCall] = useState(false);
  const lastCall = getLastCall(lead);
  const isAnswered = isCallAnswered(lastCall?.status);

  const handleCardClick = () => {
    onCardClick(lead);
  };

  const handlePhoneClick = async (e) => {
    e.stopPropagation();
    if (!lead?.leadId) return;
    setIsCalling(true);

    try {
      const token = localStorage.getItem("token");
      const payload = getJwtPayload(token);

      const loginEmail = payload?.sub || payload?.email || user?.email || "";
      const loginAccountType = payload?.accountType || user?.accountType || "";
      const loginMobileNo = payload?.mobileNumber || "";

      // 1. Determine which ID to pass to the IVR check API based on token payload
      const targetId = loginAccountType === "COMPANY"
        ? user?.id
        : (user?.companyId || payload?.companyId);
      // 2. Check the IVR API Key setup (cached per session after first success)
      const hasIvrData = await isIvrConfigured(targetId, token);

      // 3. If no IVR integration is found, show popup and stop the call
      if (!hasIvrData) {
        if (onIvrError) onIvrError();
        setIsCalling(false);
        return;
      }

      // 4. Proceed with original click-to-call functionality
      await axios.post(
        `${API_URL}/api/leads/${lead.leadId}/click-to-call`,
        {
          loginAccountType,
          loginEmail,
          loginMobileNo,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("Call initiated successfully!");
    } catch (error) {
      toast.error("Failed to initiate call. Please try again.");
      console.error("❌ Click to call Error:", error);
    } finally {
      setIsCalling(false);
    }
  };

  // Creates the video-call room via the parent handler (POST /api/videocalls)
  // and lets the parent own the resulting link/host-link modal - this button
  // only owns its own tiny "creating..." spinner state.
  const handleVideoCallClick = async (e) => {
    e.stopPropagation();
    if (!lead?.leadId || isCreatingVideoCall) return;
    setIsCreatingVideoCall(true);
    try {
      await onVideoCall(lead);
    } finally {
      setIsCreatingVideoCall(false);
    }
  };

  const handleHistoryClick = (e) => {
    e.stopPropagation();
    onOpenHistory(lead);
  };

  return (
    <div
      ref={(node) => {
        drag(node);
        if (cardRefs?.current) cardRefs.current[lead.leadId] = node;
      }}
      onClick={handleCardClick}
      className={`relative bg-white p-3 md:p-2.5 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-px transition-all duration-200 cursor-pointer mb-2 border border-gray-200 dark:bg-gray-800 dark:border-gray-700 dark:text-white min-h-[128px] ${
        lastCall
          ? `border-l-4 ${isAnswered ? "border-l-green-500" : "border-l-red-500"}`
          : ""
      } ${isDragging ? "opacity-50" : "opacity-100"
        } ${isHighlighted ? "kanban-card-highlight" : ""}`}
    >

      {/* 📞 Icon top-right, then Video Call, then merged History icon */}
      <div className="absolute top-1.5 right-1.5 hidden md:flex flex-col items-center gap-2.5 z-10">
        {lead.phone && (
          <button
            onClick={handlePhoneClick}
            disabled={isCalling}
            className={`relative flex items-center justify-center w-8 h-8 rounded-full text-white hover:bg-green-600 focus:outline-none transition-all ${isCalling ? "bg-green-700 opacity-75" : "bg-green-500"
              }`}
            title={isCalling ? "Calling..." : "Click to Call Lead"}
          >
            {/* Keep ping animation always active */}
            <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping"></span>
            {isCalling ? (
              <div className="relative z-10 flex items-center justify-center">
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <FaPhone className="relative z-10 text-xs -scale-x-100 transform" />)}
          </button>
        )}

        {/* Video Call - only rendered when the logged-in user actually has
            the videoCall permission (company owners always have it). */}
        {canVideoCall && (
          <button
            onClick={handleVideoCallClick}
            disabled={isCreatingVideoCall}
            className={`flex items-center justify-center w-7 h-7 rounded-full text-white bg-[#0EA5E9] hover:bg-[#0284C7] focus:outline-none transition-all shadow-sm ring-2 ring-white dark:ring-gray-800 ${isCreatingVideoCall ? "opacity-75" : ""
              }`}
            title={isCreatingVideoCall ? "Creating video call..." : "Start Video Call"}
          >
            {isCreatingVideoCall ? (
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Video className="w-3.5 h-3.5" />
            )}
          </button>
        )}

        {/* Merged History - opens one popup with Call History + Lead History
            as two sections/tabs inside it. */}
        <button
          onClick={handleHistoryClick}
          className="flex items-center justify-center w-7 h-7 rounded-full text-white bg-[#8B5CF6] hover:bg-[#673AB7] focus:outline-none transition-all shadow-sm ring-2 ring-white dark:ring-gray-800"
          title="History (Call & Lead)"
        >
          <History className="w-3.5 h-3.5" />
        </button>
      </div>

      {lead.projectName && (
        <span
          className="text-white px-2 py-0.5 text-xs rounded-full mb-1 inline-block max-w-full whitespace-nowrap overflow-hidden text-ellipsis"
          style={{ backgroundColor: lead.projectNameColor || "#3B82F6" }}
          title={lead.projectName}
        >
          {lead.projectName}
        </span>
      )}
      {/* pr-9 keeps all card content clear of the floating action icons on the right */}
      <div className="space-y-1 pr-0 md:pr-9">
        <p className="font-medium text-sm flex items-start">
          <span className="mr-1 mt-0.5 shrink-0">📎</span>
          <span className="break-words line-clamp-2">{lead.name}</span>
        </p>

        {lead.phone && (
          <p className="text-xs flex items-start text-gray-600 dark:text-gray-300">
            <span className="mr-1 mt-0.5 shrink-0">📞</span>
            <a
              href={googleExactSearchUrl(lead.phone)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="break-all cursor-pointer hover:text-[#673AB7] hover:underline"
              title="Search this number on Google"
            >
              {lead.phone}
            </a>
          </p>
        )}

        {accountType === "COMPANY" && lead.companyName && (
          <p className="text-xs flex items-start text-gray-600 dark:text-gray-300">
            <span className="mr-1 mt-0.5 shrink-0">🏢</span>
            <span className="break-words line-clamp-2">{lead.companyName}</span>
          </p>
        )}
        {lead.email && (
          <p className="text-xs flex items-start text-gray-600 dark:text-gray-300">
            <span className="mr-1 mt-0.5 shrink-0">✉️</span>
            <a
              href={googleExactSearchUrl(lead.email)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="break-all cursor-pointer hover:text-[#673AB7] hover:underline"
              title="Search this email on Google"
            >
              {lead.email}
            </a>
          </p>
        )}
        {lastCall && (
          <div onClick={(e) => e.stopPropagation()} className="pt-1">
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
              {/* Status pill: green = answered, red = missed / not answered */}
              <span
                className={`inline-flex items-center gap-1 min-w-0 pl-0.5 pr-2 py-0.5 rounded-full font-semibold capitalize ring-1 ring-inset ${
                  isAnswered
                    ? "bg-green-50 text-green-700 ring-green-200 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-800"
                    : "bg-red-50 text-red-700 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-800"
                }`}
              >
                <span
                  className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-white shrink-0 ${
                    isAnswered ? "bg-green-500" : "bg-red-500"
                  }`}
                >
                  {isAnswered ? (
                    <PhoneIncoming className="w-2.5 h-2.5" />
                  ) : (
                    <PhoneMissed className="w-2.5 h-2.5" />
                  )}
                </span>
                <span className="truncate">{lastCall.status || "Unknown"}</span>
              </span>

              {/* Duration (only when answered) */}
              {isAnswered && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">•</span>
                  <span className="inline-flex items-center gap-0.5 tabular-nums shrink-0">
                    <Clock className="w-3 h-3" />
                    {formatDuration(lastCall.callDuration)}
                  </span>
                </>
              )}

            </div>
          </div>
        )}
        <div className="pt-1 mt-1 border-t border-gray-100 dark:border-gray-700">
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            Created:{" "}
            {(() => {
              const date = new Date(lead.createdAt);

              const day = String(date.getDate()).padStart(2, "0");
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const year = String(date.getFullYear()).slice(-2);

              const time = date.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              });

              return `${day}-${month}-${year} ${time}`;
            })()}
          </p>

          {lead.assignedToName && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 break-words">
              Assigned: {lead.assignedToName}
            </p>
          )}
        </div>
      </div>

      {/* ── PHONE ONLY: big, labelled action buttons (desktop keeps the floating icons above) ── */}
      <div
        className="md:hidden mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex gap-1.5"
        onClick={(e) => e.stopPropagation()}
      >
        {lead.phone && (
          <button
            onClick={handlePhoneClick}
            disabled={isCalling}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[48px] rounded-lg text-white text-[11px] font-semibold active:scale-95 transition ${isCalling ? "bg-green-700 opacity-75" : "bg-green-500"}`}
          >
            {isCalling ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <FaPhone className="text-sm -scale-x-100 transform" />
            )}
            {isCalling ? "Calling" : "Call"}
          </button>
        )}
        <button
          onClick={() => onQuickStatus && onQuickStatus(lead)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[48px] rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200 text-[11px] font-semibold active:scale-95 transition"
        >
          <RefreshCw className="w-4 h-4" />
          Status
        </button>
        <button
          onClick={() => onOpenActions && onOpenActions(lead)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[48px] rounded-lg bg-[#F3E8FF] text-[#673AB7] dark:bg-purple-900/40 dark:text-purple-200 text-[11px] font-semibold active:scale-95 transition"
        >
          <MoreVertical className="w-4 h-4" />
          More
        </button>
        <button
          onClick={handleHistoryClick}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[48px] rounded-lg bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 text-[11px] font-semibold active:scale-95 transition"
        >
          <History className="w-4 h-4" />
          History
        </button>
        {canVideoCall && (
          <button
            onClick={handleVideoCallClick}
            disabled={isCreatingVideoCall}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[48px] rounded-lg bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200 text-[11px] font-semibold active:scale-95 transition ${isCreatingVideoCall ? "opacity-75" : ""}`}
          >
            {isCreatingVideoCall ? (
              <div className="w-4 h-4 border-2 border-sky-600 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Video className="w-4 h-4" />
            )}
            Video
          </button>
        )}
      </div>
    </div>
  );
}

const Card = memo(CardInner);

function ColumnInner({ column, leadsData, apiTotalCount, moveLead, accountType, onCardClick, onLoadMore, onOpenHistory, onIvrError, onVideoCall, canVideoCall, onOpenActions, onQuickStatus, highlightedLeadId, cardRefs, mobile = false }) {
  const { leads = [], hasMore = false } = leadsData || {};
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.CARD,
    drop: (item) =>
      moveLead(
        item.id,
        item.currentStatus,
        column.id,
        item.currentColor,
        column.color
      ),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      await onLoadMore(column.id);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <div
      ref={drop}
      className={`
        p-2 rounded-xl flex flex-col border transition-colors duration-200
        dark:text-white
        ${mobile ? "lm-mobile-col" : "h-[60vh] sm:h-[65vh] md:h-[70vh] lg:h-[70vh] xl:h-[70vh]"}
        ${isOver
          ? "bg-purple-50 border-[#8B5CF6] dark:bg-gray-800 dark:border-[#8B5CF6]"
          : "bg-gray-100 border-gray-200 dark:bg-gray-900 dark:border-gray-800"}
      `}
    >
      <div
        className={`${mobile ? "hidden" : "flex"} justify-between items-center px-3 py-2 rounded-lg text-white mb-2 shadow-sm`}
        style={{ backgroundColor: column.color }}
      >
        <span className="font-semibold text-xs tracking-wide truncate">{column.name}</span>
        <span className="bg-white/90 text-gray-800 text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums" title="Total Leads">
          {apiTotalCount}
        </span>
      </div>
      <div
        className="space-y-1.5 flex-1 overflow-y-auto lm-scroll pr-1 overflow-x-hidden"
      >
        {leads.map((lead) => (
          <Card
            key={lead.leadId}
            lead={lead}
            moveLead={moveLead}
            accountType={accountType}
            onCardClick={onCardClick}
            onOpenHistory={onOpenHistory}
            onIvrError={onIvrError}
            onVideoCall={onVideoCall}
            canVideoCall={canVideoCall}
            onOpenActions={onOpenActions}
            onQuickStatus={onQuickStatus}
            isHighlighted={highlightedLeadId === lead.leadId}
            cardRefs={cardRefs}
          />
        ))}
        {mobile && leads.length === 0 && (
          <div className="flex flex-col items-center justify-center text-center py-16 text-gray-500 dark:text-gray-400">
            <span className="text-3xl mb-2">📭</span>
            <p className="text-sm font-medium">No leads in {column.name}</p>
            <p className="text-xs mt-1">Swipe or tap another status above</p>
          </div>
        )}
      </div>

      {leads.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center px-1 shrink-0">
          <span className="text-[10px] sm:text-xs text-gray-700 dark:text-gray-300 font-medium">
            Showing {leads.length}{apiTotalCount ? ` of ${apiTotalCount}` : ""}
          </span>

          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className="px-4 py-2.5 md:px-2 md:py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm sm:text-xs rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoadingMore ? "Loading..." : "Load more"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const Column = memo(ColumnInner);

export default function DragAndDropKanban({ refreshTrigger }) {
  const { user } = useContext(AuthContext);
  const [leadsByStatus, setLeadsByStatus] = useState({});
  const [statusColumns, setStatusColumns] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [reason, setReason] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentDraft, setCommentDraft] = useState("");
  const [showIvrModal, setShowIvrModal] = useState(false);

  const [labels, setLabels] = useState([]);

  // ── Quick Actions (Change Status, Follow-up, Appointment, Project, SMS, WhatsApp, Email, Convert, Block)
  const [actionLead, setActionLead] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [teamUsers, setTeamUsers] = useState([]);
  const [waApiKey, setWaApiKey] = useState("");
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [showQuickStatusModal, setShowQuickStatusModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Call History popup state
  const [showCallHistoryModal, setShowCallHistoryModal] = useState(false);
  const [callHistoryLead, setCallHistoryLead] = useState(null);
  const [callHistoryData, setCallHistoryData] = useState([]);
  const [callHistoryLoading, setCallHistoryLoading] = useState(false);

  // Lead History popup state
  const [showLeadHistoryModal, setShowLeadHistoryModal] = useState(false);
  const [leadHistoryLead, setLeadHistoryLead] = useState(null);
  const [leadHistoryData, setLeadHistoryData] = useState([]);
  const [leadHistoryLoading, setLeadHistoryLoading] = useState(false);

  // Merged History popup state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyModalLead, setHistoryModalLead] = useState(null);
  const [historyModalTab, setHistoryModalTab] = useState("call");
  const [historyModalCallData, setHistoryModalCallData] = useState([]);
  const [historyModalCallLoading, setHistoryModalCallLoading] = useState(false);
  const [historyModalLeadData, setHistoryModalLeadData] = useState([]);
  const [historyModalLeadLoading, setHistoryModalLeadLoading] = useState(false);

  // Video Call popup state
  const [showVideoCallModal, setShowVideoCallModal] = useState(false);
  const [videoCallLead, setVideoCallLead] = useState(null);
  const [videoCallData, setVideoCallData] = useState(null);

  // ── Lead Search State ─────────────────────────────────────────────────────
  const [searchType, setSearchType] = useState("phone");
  const [leadSearchQuery, setLeadSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [activeSearchIndex, setActiveSearchIndex] = useState(-1);
  const [highlightedLeadId, setHighlightedLeadId] = useState(null);
  const searchWrapRef = useRef(null);
  const leadCardRefs = useRef({});
  const highlightTimeoutRef = useRef(null);

  // ── Phone layout (below Tailwind `md`): one status at a time + swipeable tab strip ──
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  const [mobileStatusId, setMobileStatusId] = useState(null);
  const mobileTabsRef = useRef(null);
  const swipeRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // keep the active status chip visible in the tab strip
  useEffect(() => {
    if (!isMobile || !mobileTabsRef.current) return;
    const el = mobileTabsRef.current.querySelector('[data-active="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [isMobile, mobileStatusId, statusColumns.length]);

  // Prev / Next navigation inside the lead details popup (phone footer)
  const leadNav = useMemo(() => {
    if (!selectedLead) return null;
    const list = leadsByStatus[selectedLead.statusName]?.leads || [];
    const idx = list.findIndex((l) => String(l.leadId) === String(selectedLead.leadId));
    if (idx === -1) return null;
    return {
      index: idx,
      total: list.length,
      onPrev: idx > 0 ? () => setSelectedLead(list[idx - 1]) : null,
      onNext: idx < list.length - 1 ? () => setSelectedLead(list[idx + 1]) : null,
    };
  }, [selectedLead, leadsByStatus]);

  const token = user?.token || localStorage.getItem("token");
  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  const [canVideoCall, setCanVideoCall] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      setCanVideoCall(false);
      return;
    }
    let cancelled = false;
    const fetchVideoCallPermission = async () => {
      try {
        const url =
          user.accountType === "SUPER_SUPER_ADMIN" ||
            user.accountType === "SUPER_ADMIN" ||
            user.accountType === "RESELLER" ||
            user.accountType === "COMPANY"
            ? `${API_URL}/api/user/details/${user.id}`
            : `${API_URL}/api/companyuser/details/${user.id}`;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const ownPermission = response.data?.permission?.[0] || {};
        if (!cancelled) {
          setCanVideoCall(ownPermission.videoCall === true);
        }
      } catch (err) {
        if (!cancelled) {
          setCanVideoCall(false);
        }
      }
    };
    fetchVideoCallPermission();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.accountType, token]);

  const fetchStatusCounts = useCallback(async () => {
    if (!user?.id) return;
    const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;
    if (!companyId) return;

    try {
      const response = await axios.get(`${API_URL}/api/leads/status-wise-leads`, {
        params: {
          companyId: companyId,
          userId: user.id,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const countsMap = {};
      response.data.forEach((item) => {
        countsMap[(item.statusName || "").trim()] = item.totalLeads;
      });
      setStatusCounts(countsMap);
    } catch (err) {
      console.error("Error fetching status counts:", err);
    }
  }, [user, token]);

  const fetchStatusColumns = useCallback(async () => {
    const companyId =
      user?.accountType === "COMPANY" ? user?.id : user?.companyId;
    if (!companyId) return;

    try {
      const response = await axios.get(
        `${API_URL}/api/leadStatus/company/${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const prioritySequence = [
        "New",
        "Process",
        "Pending",
        "Confirm",
        "Cancel",
      ];

      const activeColumnsFromApi = response.data
        .filter((status) => {
          const isActive = status.active !== false;
          return isActive;
        })
        .map((status) => ({
          ...status,
          name: (status.name || "").trim(),
        }))
        .sort((a, b) => {
          const aIndex = prioritySequence.indexOf(a.name);
          const bIndex = prioritySequence.indexOf(b.name);

          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }

          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;

          return a.leadStatusId.localeCompare(b.leadStatusId);
        })
        .map((status) => ({
          id: status.name,
          name: status.name,
          color: status.color,
        }));

      setStatusColumns(activeColumnsFromApi);
    } catch (err) {
      console.error("Error fetching status columns:", err);
      setStatusColumns([]);
    }
  }, [user, token]);

  const fetchPage = useCallback(async () => {
    if (!user?.id || !user?.accountType) return;

    try {
      const companyId = user.accountType === "COMPANY" ? user.id : user.companyId;

      const url = `${API_URL}/api/leads/company/${companyId}/status-wise?limit=${PAGE_SIZE}&userId=${user.id}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const statusesData = response.data?.statuses || [];
      const nextLeadsByStatus = {};

      statusesData.forEach((statusItem) => {
        const key = (statusItem.statusName || "").trim();
        nextLeadsByStatus[key] = {
          leads: statusItem.leads || [],
          hasMore: !!statusItem.hasMore,
        };
      });

      setLeadsByStatus(nextLeadsByStatus);

      if (response.data?.fieldDefinitions) {
        setFieldDefinitions(response.data.fieldDefinitions);
      }
    } catch (err) {
      console.error("Error fetching leads:", err);
    }
  }, [user, token]);

  const leadsByStatusRef = useRef(leadsByStatus);
  leadsByStatusRef.current = leadsByStatus;

  const loadMoreForStatus = useCallback(
    async (statusName) => {
      const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;
      if (!companyId) return;

      const current = leadsByStatusRef.current[statusName];
      if (!current || !current.hasMore) return;

      const lastLead = current.leads[current.leads.length - 1];

      try {
        const response = await axios.get(
          `${API_URL}/api/leads/company/${companyId}/status/${encodeURIComponent(statusName)}`,
          {
            params: {
              userId: user.id,
              limit: PAGE_SIZE,
              ...(lastLead
                ? { afterCreatedAt: lastLead.createdAt, afterId: lastLead.leadId }
                : {}),
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const moreLeads = response.data?.leads || [];
        const hasMore = !!response.data?.hasMore;

        setLeadsByStatus((prev) => ({
          ...prev,
          [statusName]: {
            leads: [...(prev[statusName]?.leads || []), ...moreLeads],
            hasMore,
          },
        }));
      } catch (err) {
        console.error(`Error loading more leads for status "${statusName}":`, err);
      }
    },
    [user, token]
  );

  const fetchLabels = useCallback(async () => {
    const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;
    if (!companyId) return;

    try {
      const response = await axios.get(`${API_URL}/api/projectName/company/${companyId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLabels(response.data || []);
    } catch (err) {
      console.error("Error fetching project labels:", err);
    }
  }, [user, token]);

  const fetchTeamUsers = useCallback(async () => {
    if (!user?.id || !user?.accountType) return;
    try {
      const isOwner = ["COMPANY", "SUPER_SUPER_ADMIN", "SUPER_ADMIN", "RESELLER"].includes(user.accountType);
      const usersUrl = isOwner
        ? `${API_URL}/api/companyuser/getByUserId?userId=${user.id}&accountType=${user.accountType}`
        : `${API_URL}/api/companyuser/getSameAndSubordinateUsers?userId=${user.id}&accountType=${user.accountType}`;
      const response = await axios.get(usersUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeamUsers(response.data || []);
    } catch (err) {
      console.error("Error fetching team users:", err);
    }
  }, [user, token]);

  const fetchWaConfig = useCallback(async () => {
    const compId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;
    if (!compId) return;
    try {
      const res = await axios.get(`${API_URL}/api/whatsappConfig/${compId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res?.data?.status === "success") {
        setWaApiKey(res.data.data.apiKey);
      }
    } catch (err) {
      console.warn("WhatsApp config not found or error fetching:", err.message);
    }
  }, [user, token]);

  const hasLoadedOnceRef = useRef(false);

  const fetchData = async () => {
    // Side data loads in parallel with the board instead of after it.
    fetchLabels();
    fetchTeamUsers();
    fetchWaConfig();
    try {
      // Only show the full-page spinner on the very first load; later
      // refreshes update the board in place instead of unmounting it.
      if (!hasLoadedOnceRef.current) setLoading(true);
      await Promise.all([fetchStatusColumns(), fetchStatusCounts(), fetchPage()]);
    } catch (err) {
      setError(err.message);
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || !user?.id || !user?.accountType) return;
    fetchData();
  }, [fetchStatusColumns, fetchStatusCounts, fetchPage, fetchLabels, fetchTeamUsers, fetchWaConfig, user, refreshTrigger]);

  useEffect(() => {
    const handleLeadCreated = () => {
      fetchData();
    };

    window.addEventListener("leadCreated", handleLeadCreated);

    return () => {
      window.removeEventListener("leadCreated", handleLeadCreated);
    };
  }, []);

  // ── Lead Search Setup ─────────────────────────────────────────────────────
  const allLoadedLeads = useMemo(() => {
    return Object.values(leadsByStatus).reduce((acc, col) => acc.concat(col.leads || []), []);
  }, [leadsByStatus]);

  const matchedSearchLeads = useMemo(() => {
    const q = leadSearchQuery.trim().toLowerCase();
    if (!q) return [];
    return allLoadedLeads.filter(l => {
      if (searchType === "phone") return l.phone && String(l.phone).includes(q);
      if (searchType === "name") return l.name && l.name.toLowerCase().includes(q);
      return false;
    }).slice(0, 8); // Top 8 results
  }, [leadSearchQuery, allLoadedLeads, searchType]);

  useEffect(() => {
    setActiveSearchIndex(-1);
  }, [leadSearchQuery]);

  useEffect(() => {
    if (showSearchDropdown && activeSearchIndex >= 0) {
      const el = document.getElementById(`lead-search-item-${activeSearchIndex}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activeSearchIndex, showSearchDropdown]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  const handleSelectSearchLead = useCallback((lead) => {
    if (!lead) return;
    setShowSearchDropdown(false);
    const reveal = () => {
      const el = leadCardRefs.current[lead.leadId];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });

        if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        setHighlightedLeadId(lead.leadId);

        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedLeadId(null);
        }, 2000);
      } else {
        toast.info("This lead is not currently loaded in the view. You may need to load more leads.");
      }
    };
    // On phones only the active status is rendered, so switch to the lead's status first.
    if (window.matchMedia("(max-width: 767px)").matches) {
      if (lead.statusName) setMobileStatusId(lead.statusName);
      setTimeout(reveal, 200);
    } else {
      reveal();
    }
  }, []);

  const handleApiSearch = async () => {
    const keyword = leadSearchQuery.trim();
    if (!keyword) return;

    const hasLetters = /[a-zA-Z]/.test(keyword);
    const isMostlyNumbers = /^\d+$/.test(keyword.replace(/[\s\+\-\(\)]/g, ""));

    if (searchType === "phone" && hasLetters) {
      toast.error("No leads found matching the search criteria");
      return;
    }
    if (searchType === "name" && isMostlyNumbers) {
      toast.error("No leads found matching the search criteria");
      return;
    }

    setIsSearching(true);
    setShowSearchDropdown(false);

    try {
      const res = await axios.get(`${API_URL}/api/leads/search/${companyId}/${user?.id}`, {
        params: { [searchType]: keyword, limit: 10 },
        headers: { Authorization: `Bearer ${token}` }
      });

      const fetchedLeads = res.data?.leads || (Array.isArray(res.data) ? res.data : []);
      const leadsArray = Array.isArray(fetchedLeads) ? fetchedLeads : (fetchedLeads.content || []);

      if (leadsArray.length === 0) {
        toast.error("No leads found matching the search criteria");
        return;
      }

      setLeadsByStatus((prev) => {
        const next = { ...prev };
        leadsArray.forEach((lead) => {
          const status = lead.statusName;
          const bucket = next[status] || { leads: [], hasMore: false };
          next[status] = {
            ...bucket,
            leads: [lead, ...bucket.leads.filter((l) => l.leadId !== lead.leadId)],
          };
        });
        return next;
      });

      setTimeout(() => {
        handleSelectSearchLead(leadsArray[0]);
      }, 300);

    } catch (err) {
      console.error("Search API Error:", err);
      toast.error("No leads found matching the search criteria");
    } finally {
      setIsSearching(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus, newColor, reasonText) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/leads/update/${leadId}/changedBy/${user?.id}`,
        {
          statusName: newStatus,
          statusColor: newColor,
          reason: reasonText,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === "Successful") {
        return response.data.updatedLead;
      } else {
        throw new Error(response.data.message || "Failed to update status");
      }
    } catch (err) {
      console.error("Error updating lead status:", err);
      throw err;
    }
  };

  const moveLead = useCallback((
    leadId,
    currentStatus,
    newStatus,
    currentColor,
    newColor
  ) => {
    if (currentStatus === newStatus) return;
    setPendingUpdate({
      leadId,
      currentStatus,
      newStatus,
      currentColor,
      newColor,
    });
    setShowModal(true);
  }, []);

  const relocateLead = (leadId, fromStatus, toStatus, leadPatchOrObject) => {
    setLeadsByStatus((prev) => {
      const next = { ...prev };

      const fromBucket = next[fromStatus];
      let movedLead = fromBucket?.leads.find((l) => l.leadId === leadId);

      if (fromBucket) {
        next[fromStatus] = {
          ...fromBucket,
          leads: fromBucket.leads.filter((l) => l.leadId !== leadId),
        };
      }

      const finalLead = leadPatchOrObject.leadId
        ? leadPatchOrObject
        : { ...(movedLead || { leadId }), ...leadPatchOrObject };

      const toBucket = next[toStatus] || { leads: [], hasMore: false };
      next[toStatus] = {
        ...toBucket,
        leads: [finalLead, ...toBucket.leads],
      };

      return next;
    });
  };

  const handleModalSubmit = async () => {
    if (!pendingUpdate) return;
    const { leadId, currentStatus, newStatus, newColor } = pendingUpdate;
    const snapshot = leadsByStatus;

    try {
      // Optimistic move for instant drag feedback
      relocateLead(leadId, currentStatus, newStatus, {
        statusName: newStatus,
        statusColor: newColor,
      });

      // Pass trimmed reason to avoid just saving trailing spaces
      const updatedLead = await updateLeadStatus(
        leadId,
        newStatus,
        newColor,
        reason.trim()
      );

      if (updatedLead) {
        // Reconcile with the server's version of the lead (in case anything
        // else besides status/color changed as part of the update).
        relocateLead(leadId, newStatus, newStatus, updatedLead);
        // Refresh the exact counts globally after status change
        fetchStatusCounts();
      }

      toast.success(`Lead status updated to ${newStatus}`);
    } catch (err) {
      setLeadsByStatus(snapshot); // revert optimistic move
      toast.error(
        err.response?.data?.message ||
        err.message ||
        "Failed to update lead status"
      );
    } finally {
      setShowModal(false);
      setReason("");
      setPendingUpdate(null);
    }
  };

  const patchLeadEverywhere = useCallback((leadId, statusName, patch) => {
    setSelectedLead((prev) =>
      prev && String(prev.leadId) === String(leadId) ? { ...prev, ...patch } : prev
    );

    setLeadsByStatus((prev) => {
      const bucket = prev[statusName];
      if (!bucket) return prev;
      return {
        ...prev,
        [statusName]: {
          ...bucket,
          leads: bucket.leads.map((l) =>
            String(l.leadId) === String(leadId) ? { ...l, ...patch } : l
          ),
        },
      };
    });
  }, []);

  // Patches a lead wherever it is shown (board, details popup, history popups)
  // without needing to know which status column it currently sits in.
  const patchLeadById = useCallback((leadId, patch) => {
    const same = (l) => String(l.leadId) === String(leadId);
    setSelectedLead((prev) => (prev && same(prev) ? { ...prev, ...patch } : prev));
    setCallHistoryLead((prev) => (prev && same(prev) ? { ...prev, ...patch } : prev));
    setHistoryModalLead((prev) => (prev && same(prev) ? { ...prev, ...patch } : prev));
    setLeadsByStatus((prev) => {
      let changed = false;
      const next = {};
      Object.keys(prev).forEach((key) => {
        const bucket = prev[key];
        if (bucket.leads?.some(same)) {
          changed = true;
          next[key] = { ...bucket, leads: bucket.leads.map((l) => (same(l) ? { ...l, ...patch } : l)) };
        } else {
          next[key] = bucket;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const patchActionLeadEverywhere = (leadId, statusName, patch) => {
    patchLeadEverywhere(leadId, statusName, patch);
    setActionLead((prev) =>
      prev && String(prev.leadId) === String(leadId) ? { ...prev, ...patch } : prev
    );
  };

  const handleInlineEdit = useCallback(async (field, value) => {
    if (!selectedLead) return;
    try {
      await axios.put(
        `${API_URL}/api/leads/update/${selectedLead.leadId}/changedBy/${user?.id}`,
        { [field]: value },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      patchLeadEverywhere(selectedLead.leadId, selectedLead.statusName, { [field]: value });
      toast.success("Lead updated successfully!");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.message || "A lead with this number already exists");
      } else {
        toast.error("Failed to update lead details.");
      }
      console.error("Failed to update field", err);
    }
  }, [selectedLead, user?.id, token, patchLeadEverywhere]);

  const handleCustomFieldEdit = useCallback(async (field, value) => {
    if (!selectedLead) return;
    try {
      const updatedCustomFields = {
        ...(selectedLead.customFields || {}),
        [field]: value,
      };

      await axios.put(
        `${API_URL}/api/leads/update/${selectedLead.leadId}/changedBy/${user?.id}`,
        {
          [field]: value,
          customFields: updatedCustomFields,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      patchLeadEverywhere(selectedLead.leadId, selectedLead.statusName, {
        [field]: value,
        customFields: updatedCustomFields,
      });

      toast.success("Lead updated successfully!");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.message || "Conflict updating field");
      } else {
        toast.error("Failed to update lead details.");
      }
      console.error("Failed to update custom field", err);
    }
  }, [selectedLead, user?.id, token, patchLeadEverywhere]);

  const openCommentModal = useCallback(() => {
    setCommentDraft(selectedLead?.comment || "");
    setShowCommentModal(true);
  }, [selectedLead]);

  const handleCommentSave = async () => {
    if (commentDraft.length > 0 && commentDraft.trim().length === 0) {
      toast.warning("Comment cannot contain only spaces.");
      return;
    }
    await handleInlineEdit("comment", commentDraft.trim());
    setShowCommentModal(false);
  };

  const handleOpenCallHistory = useCallback(async (lead) => {
    if (!lead?.leadId) return;
    setCallHistoryLead(lead);
    setCallHistoryLoading(true);
    setShowCallHistoryModal(true);
    try {
      const response = await axios.get(`${API_URL}/api/leads/${lead.leadId}/call-recordings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCallHistoryData(response.data || []);
    } catch (err) {
      console.error("Error fetching call history:", err);
      toast.error("Failed to fetch call history");
      setCallHistoryData([]);
    } finally {
      setCallHistoryLoading(false);
    }
  }, [token]);

  const handleOpenLeadHistory = useCallback(async (lead) => {
    if (!lead?.leadId) return;
    setLeadHistoryLead(lead);
    setLeadHistoryLoading(true);
    setShowLeadHistoryModal(true);
    try {
      const response = await axios.get(`${API_URL}/api/leadHistory/${lead.leadId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLeadHistoryData(response.data?.content || []);
    } catch (err) {
      console.error("Error fetching lead history:", err);
      toast.error("Failed to fetch lead history");
      setLeadHistoryData([]);
    } finally {
      setLeadHistoryLoading(false);
    }
  }, [token]);

  const handleOpenHistory = useCallback(async (lead) => {
    if (!lead?.leadId) return;
    setHistoryModalLead(lead);
    setHistoryModalTab("call");
    setShowHistoryModal(true);

    setHistoryModalCallLoading(true);
    setHistoryModalLeadLoading(true);

    const headers = { Authorization: `Bearer ${token}` };

    // Both requests fire at the same time instead of one after the other.
    const callPromise = axios
      .get(`${API_URL}/api/leads/${lead.leadId}/call-recordings`, { headers })
      .then((response) => setHistoryModalCallData(response.data || []))
      .catch((err) => {
        console.error("Error fetching call history:", err);
        toast.error("Failed to fetch call history");
        setHistoryModalCallData([]);
      })
      .finally(() => setHistoryModalCallLoading(false));

    const historyPromise = axios
      .get(`${API_URL}/api/leadHistory/${lead.leadId}`, { headers })
      .then((response) => setHistoryModalLeadData(response.data?.content || []))
      .catch((err) => {
        console.error("Error fetching lead history:", err);
        toast.error("Failed to fetch lead history");
        setHistoryModalLeadData([]);
      })
      .finally(() => setHistoryModalLeadLoading(false));

    await Promise.all([callPromise, historyPromise]);
  }, [token]);

  const handleCreateVideoCall = useCallback(async (lead) => {
    if (!lead?.leadId) return;
    try {
      const response = await axios.post(
        `${API_URL}/api/videocalls`,
        {
          leadId: lead.leadId,
          leadName: lead.name || "",
          password: null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setVideoCallLead(lead);
      setVideoCallData(response.data);
      setShowVideoCallModal(true);
    } catch (err) {
      console.error("Error creating video call:", err);
      if (err.response?.status === 403) {
        toast.error("You don't have permission to start video calls.");
      } else if (err.response?.status === 400) {
        toast.error("Lead is required to start a video call.");
      } else {
        toast.error("Failed to create video call. Please try again.");
      }
    }
  }, [token]);

  // ── Quick Actions menu ──────────────────────────────────────────────────
  const openActionsMenu = useCallback((lead) => {
    if (!lead) return;
    setActionLead(lead);
    setShowActionsMenu(true);
  }, []);

  // Phone shortcut: open "Change Status" directly from the card
  const openQuickStatus = useCallback((lead) => {
    if (!lead) return;
    setActionLead(lead);
    setShowQuickStatusModal(true);
  }, []);

  const handleCardClick = useCallback((lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  }, []);

  const handleIvrError = useCallback(() => setShowIvrModal(true), []);
  const closeLeadModal = useCallback(() => setShowLeadModal(false), []);
  const closeActionsMenu = () => setShowActionsMenu(false);

  const handleQuickStatusChange = async (statusName, reasonText) => {
    if (!actionLead) return;
    const status = statusColumns.find((s) => s.name === statusName);
    try {
      const updatedLead = await updateLeadStatus(actionLead.leadId, statusName, status?.color, reasonText);
      relocateLead(
        actionLead.leadId,
        actionLead.statusName,
        statusName,
        updatedLead || { statusName, statusColor: status?.color }
      );
      fetchStatusCounts();
      setActionLead((prev) => (prev ? { ...prev, statusName, statusColor: status?.color } : prev));
      toast.success(`Lead status updated to ${statusName}`);
      setShowQuickStatusModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to update lead status");
    }
  };

  const handleFollowUpSubmit = async (data) => {
    if (!actionLead) return;
    try {
      await axios.post(
        `${API_URL}/api/leads/${actionLead.leadId}/followups`,
        { ...data, createdById: user?.id, companyId },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      toast.success("Follow-up added successfully!");
      setShowFollowUpModal(false);
    } catch (err) {
      console.error("Error adding follow-up:", err);
      toast.error("Failed to add follow-up.");
    }
  };

  const handleAssignProject = async (labelId, labelName) => {
    if (!actionLead) return;
    try {
      await axios.put(
        `${API_URL}/api/leads/assignProjectName/${labelId}/${user?.id}`,
        [actionLead.leadId],
        { headers: { Authorization: `Bearer ${token}` } }
      );
      patchActionLeadEverywhere(actionLead.leadId, actionLead.statusName, { projectName: labelName });
      toast.success("Project assigned successfully!");
      setShowLabelModal(false);
    } catch (err) {
      console.error("Error assigning project:", err);
      toast.error("Failed to assign project.");
    }
  };

  const handleConvertLead = async () => {
    if (!actionLead) return;
    try {
      await axios.post(
        `${API_URL}/api/convertedLeads`,
        { ...actionLead, companyId, convertedById: user?.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      patchActionLeadEverywhere(actionLead.leadId, actionLead.statusName, { isConvertedLead: true });
      toast.success("Lead converted to customer!");
      setShowConvertModal(false);
    } catch (err) {
      console.error("Error converting lead:", err);
      toast.error("Failed to convert lead.");
    }
  };

  const handleBlockLead = async (reasonText) => {
    if (!actionLead) return;
    try {
      await axios.post(
        `${API_URL}/api/blacklist`,
        { createdById: user?.id, leadId: actionLead.leadId, reason: reasonText, companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeadsByStatus((prev) => {
        const bucket = prev[actionLead.statusName];
        if (!bucket) return prev;
        return {
          ...prev,
          [actionLead.statusName]: {
            ...bucket,
            leads: bucket.leads.filter((l) => String(l.leadId) !== String(actionLead.leadId)),
          },
        };
      });
      setStatusCounts((prev) => ({
        ...prev,
        [actionLead.statusName]: Math.max(0, (prev[actionLead.statusName] || 0) - 1),
      }));
      toast.success("Lead blocked successfully.");
      setShowBanModal(false);
      setShowLeadModal(false);
    } catch (err) {
      console.error("Error blocking lead:", err);
      toast.error("Failed to block lead.");
    }
  };

  const handleOpenSmsModal = async () => {
    if (!actionLead) return;
    try {
      const res = await axios.get(`${API_URL}/api/smsTemplates/company/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSmsTemplates(res.data?.smsTemplates || res.data || []);
    } catch (err) {
      console.error("Failed to fetch SMS templates:", err);
      toast.error("Failed to load SMS templates.");
    }
    setShowSmsModal(true);
  };

  const handleOpenEmailModal = async () => {
    if (!actionLead) return;
    try {
      const res = await axios.get(`${API_URL}/api/emailTemplates/company/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmailTemplates(res.data?.emailTemplates || res.data || []);
    } catch (err) {
      console.error("Failed to fetch Email templates:", err);
      toast.error("Failed to load Email templates.");
    }
    setShowEmailModal(true);
  };

  const handleOpenWhatsAppModal = async () => {
    if (!actionLead) return;
    if (!waApiKey) {
      toast.error("WhatsApp API Key is not configured for this company.");
      return;
    }
    try {
      const res = await axios.get(`https://waapi.deetyasoft.com/api/lms/whatsapp-config/getTemplate?apiKey=${waApiKey}`);
      setWhatsappTemplates(res.data?.templates || res.data?.data || res.data || []);
      setShowWhatsAppModal(true);
    } catch (err) {
      console.error("Failed to fetch WhatsApp templates:", err);
      toast.error("Failed to load WhatsApp templates.");
    }
  };

  const handleSendSms = async (messageId, variables) => {
    if (!actionLead?.phone) return toast.error("Lead has no phone number");
    try {
      await axios.post(
        `${API_URL}/api/smsConfig/send/${companyId}?createdById=${user?.id}`,
        { clientNumber: [actionLead.phone], messageId, variables },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      toast.success("SMS sent successfully!");
      setShowSmsModal(false);
    } catch (err) {
      console.error("Error sending SMS:", err);
      toast.error(err.response?.data?.message || "Failed to send SMS message.");
    }
  };

  const handleSendEmail = async (templateId, variables) => {
    if (!actionLead?.email) return toast.error("Lead has no email address");
    setSendingEmailId(templateId);
    try {
      const response = await fetch(
        `${API_URL}/api/leads/${templateId}/sendEmail/${companyId}/${user?.id}/${actionLead.email}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify(variables),
        }
      );
      if (response.ok) {
        toast.success("Email sent successfully!");
        setShowEmailModal(false);
      } else {
        toast.error("Failed to send Email.");
      }
    } catch (err) {
      console.error("Error sending Email:", err);
      toast.error("Failed to send Email message.");
    } finally {
      setSendingEmailId(null);
    }
  };

  const handleSendWhatsApp = async (templateName, variableValues = {}) => {
    if (!waApiKey) return toast.error("WhatsApp API Key is missing!");
    if (!actionLead?.phone) return toast.error("Lead has no phone number");

    const cleaned = actionLead.phone.replace(/\D/g, "");
    const phone = cleaned.startsWith("91") && cleaned.length === 12 ? cleaned : cleaned.length === 10 ? `91${cleaned}` : cleaned;

    const fd = new FormData();
    fd.append("apiKey", waApiKey);
    fd.append("templateName", templateName);
    fd.append("to", phone);

    const varKeys = Object.keys(variableValues).sort((a, b) => parseInt(a) - parseInt(b));
    if (varKeys.length > 0) {
      fd.append("manualVars", varKeys.map((key) => variableValues[key] || "").join(","));
    }

    try {
      await axios.post(`https://waapi.deetyasoft.com/api/lms/whatsapp-config/sendVerifiedTemplates`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("WhatsApp message sent!");
      setShowWhatsAppModal(false);
    } catch (err) {
      console.error("Error sending WhatsApp:", err);
      toast.error("Failed to send WhatsApp message.");
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-[#673AB7]"></div>
      </div>
    );

  if (error)
    return (
      <div className="text-center p-4 text-red-500">
        Error: {error}
        <button
          onClick={() => window.location.reload()}
          className="ml-2 px-4 py-2 bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] transition"
        >
          Retry
        </button>
      </div>
    );

  const activeMobileId =
    statusColumns.find((c) => c.id === mobileStatusId)?.id ?? statusColumns[0]?.id;
  const activeMobileColumn = statusColumns.find((c) => c.id === activeMobileId);

  const handleSwipeStart = (e) => {
    const t = e.touches[0];
    swipeRef.current = { x: t.clientX, y: t.clientY };
  };
  const handleSwipeEnd = (e) => {
    const start = swipeRef.current;
    swipeRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 2) return;
    const idx = statusColumns.findIndex((c) => c.id === activeMobileId);
    const next = statusColumns[idx + (dx < 0 ? 1 : -1)];
    if (next) setMobileStatusId(next.id);
  };

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <div className="p-2 cfg-font">
          <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap');
            .cfg-heading { font-family: 'Sora', sans-serif; color: #1F2937; }
            .cfg-font { font-family: 'Inter', sans-serif; }
            
            .kanban-search-wrap {
              position: relative;
              display: flex;
              align-items: center;
              border: 1px solid #e5e9f0;
              border-radius: 8px;
              background: #f8fafc;
              transition: border-color .15s, box-shadow .15s, background .15s;
            }
            .kanban-search-wrap:focus-within {
              border-color: #673AB7;
              box-shadow: 0 0 0 3px rgba(103,58,183,.14);
              background: #fff;
            }
            .kanban-search-select {
              padding: 7px 2px 7px 8px;
              border: none;
              background: transparent;
              outline: none;
              font-size: 13px;
              color: #1a1f36;
              cursor: pointer;
            }
            .kanban-search-input {
              padding: 7px 12px 7px 6px;
              border: none;
              background: transparent;
              outline: none;
              font-size: 13px;
              font-family: inherit;
              color: #1a1f36;
              width: 180px;
            }
            .kanban-search-dropdown { position: absolute; top: calc(100% + 6px); right: 0; background: #fff; border: 1px solid #e5e9f0; border-radius: 10px; box-shadow: 0 8px 30px rgba(0,0,0,.12); max-height: 260px; width: 300px; overflow-y: auto; z-index: 300; }
            .kanban-search-dropdown-item { display: flex; align-items: center; gap: 8px; padding: 10px 12px; font-size: 13px; font-weight: 500; color: #1a1f36; cursor: pointer; transition: background .1s, color .1s; border-bottom: 1px solid #f1f5f9; }
            .kanban-search-dropdown-item:last-child { border-bottom: none; }
            .kanban-search-dropdown-item:hover, .kanban-search-dropdown-item--active { background: #f3e8ff; color: #673AB7; }
            .kanban-search-dropdown-empty { padding: 12px; font-size: 12px; color: #9ca3af; text-align: center; }
            
            .dark .kanban-search-wrap { border-color: #374151; background: #1f2937; }
            .dark .kanban-search-select, .dark .kanban-search-input { color: #fff; }
            
            /* Smooth, contained scrolling for the board columns, modals and every
               nested box (transcripts, follow-ups, extraction lists). */
            .lm-scroll {
              scroll-behavior: smooth;
              overscroll-behavior: contain;
              -webkit-overflow-scrolling: touch;
              scrollbar-width: thin;
              scrollbar-color: rgba(103,58,183,.35) transparent;
              scrollbar-gutter: stable;
            }
            .lm-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
            .lm-scroll::-webkit-scrollbar-track { background: transparent; }
            .lm-scroll::-webkit-scrollbar-thumb { background: rgba(103,58,183,.28); border-radius: 999px; }
            .lm-scroll::-webkit-scrollbar-thumb:hover { background: rgba(103,58,183,.5); }
            @media (prefers-reduced-motion: reduce) { .lm-scroll { scroll-behavior: auto; } }

            .lm-overlay-in { animation: lmOverlayIn .16s ease-out both; }
            .lm-modal-pop { animation: lmModalPop .2s cubic-bezier(.2,.8,.2,1) both; }
            @keyframes lmOverlayIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes lmModalPop { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }
            @media (prefers-reduced-motion: reduce) { .lm-overlay-in, .lm-modal-pop { animation: none; } }

            /* ── Phone layout (desktop unaffected) ── */
            .lm-tabs-strip { scrollbar-width: none; -webkit-overflow-scrolling: touch; }
            .lm-tabs-strip::-webkit-scrollbar { display: none; }
            .lm-mobile-col { height: calc(100vh - 250px); height: calc(100dvh - 250px); min-height: 320px; }
            @media (max-width: 767px) {
              .kanban-search-wrap { width: 100%; }
              .kanban-search-select { font-size: 16px; padding: 11px 2px 11px 10px; }
              .kanban-search-input { font-size: 16px; padding: 11px 8px; width: 100%; min-width: 0; flex: 1; }
              .kanban-search-dropdown { left: 0; right: 0; width: auto; max-height: 50vh; }
              .kanban-search-dropdown-item { padding: 13px 12px; }
              /* every popup becomes a bottom sheet */
              .lm-overlay-in { align-items: flex-end !important; padding: 0 !important; }
              .lm-overlay-in > div { width: 100% !important; max-width: 100% !important; border-bottom-left-radius: 0 !important; border-bottom-right-radius: 0 !important; border-top-left-radius: 16px !important; border-top-right-radius: 16px !important; max-height: 92vh; max-height: 92dvh; overflow-y: auto; }
              .lm-overlay-in input, .lm-overlay-in textarea, .lm-overlay-in select { font-size: 16px !important; }
              .lm-overlay-in button { min-height: 40px; }
            }

            .kanban-card-highlight { animation: kanbanHighlightShrink 2s ease-in-out forwards; z-index: 10; position: relative; }
            @keyframes kanbanHighlightShrink {
                0%   { transform: scale(1); box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 8px 24px -12px rgba(16,24,40,.10); border-color: transparent; }
                15%  { transform: scale(0.92); box-shadow: -4px -4px 15px #e9d5ff, 4px 4px 15px #a78bfa, 0 0 0 3px #d8b4fe; border-color: #d8b4fe; }
                85%  { transform: scale(0.92); box-shadow: -4px -4px 15px #e9d5ff, 4px 4px 15px #a78bfa, 0 0 0 3px #d8b4fe; border-color: #d8b4fe; }
                100% { transform: scale(1); box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 8px 24px -12px rgba(16,24,40,.10); border-color: transparent; }
            }
          `}</style>

          <div className="flex flex-col gap-2 md:flex-row md:justify-between md:items-center mb-3 md:mb-4">
            <h2 className="text-base md:text-lg font-bold cfg-heading dark:text-white">
              🗂️ Lead Management Board
            </h2>
            <div className="kanban-search-wrap w-full md:w-auto dark:bg-gray-800 dark:border-gray-700" ref={searchWrapRef}>
              <select
                value={searchType}
                onChange={(e) => {
                  setSearchType(e.target.value);
                  setLeadSearchQuery("");
                }}
                className="kanban-search-select dark:text-white"
              >
                <option value="phone">Phone No</option>
                <option value="name">Name</option>
              </select>
              <input
                type={searchType === "phone" ? "tel" : "text"}
                inputMode={searchType === "phone" ? "numeric" : undefined}
                className="kanban-search-input dark:text-white"
                placeholder={searchType === "phone" ? "Phone no..." : "Name..."}
                value={leadSearchQuery}
                onChange={(e) => {
                  setLeadSearchQuery(e.target.value);
                  setShowSearchDropdown(true);
                }}
                onFocus={() => {
                  if (leadSearchQuery.trim()) setShowSearchDropdown(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    if (matchedSearchLeads.length === 0) return;
                    setShowSearchDropdown(true);
                    setActiveSearchIndex((prev) => prev < matchedSearchLeads.length - 1 ? prev + 1 : 0);
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    if (matchedSearchLeads.length === 0) return;
                    setShowSearchDropdown(true);
                    setActiveSearchIndex((prev) => prev > 0 ? prev - 1 : matchedSearchLeads.length - 1);
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    if (showSearchDropdown && activeSearchIndex >= 0 && matchedSearchLeads.length > 0) {
                      handleSelectSearchLead(matchedSearchLeads[activeSearchIndex]);
                    } else {
                      handleApiSearch();
                    }
                  } else if (e.key === "Escape") {
                    setShowSearchDropdown(false);
                  }
                }}
              />
              <button onClick={handleApiSearch} className="pr-3 pl-1 text-[#673AB7] hover:text-[#5E35A6]">
                {isSearching ? (
                  <div className="w-4 h-4 border-2 border-[#673AB7] border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </button>

              {showSearchDropdown && leadSearchQuery.trim() && (
                <div className="kanban-search-dropdown dark:bg-gray-800 dark:border-gray-700">
                  {matchedSearchLeads.length === 0 ? (
                    <div className="kanban-search-dropdown-empty">Press Enter to search all leads...</div>
                  ) : (
                    matchedSearchLeads.map((l, idx) => (
                      <div
                        key={l.leadId}
                        id={`lead-search-item-${idx}`}
                        className={`kanban-search-dropdown-item dark:text-gray-200 dark:hover:bg-gray-700 dark:border-gray-700 ${idx === activeSearchIndex ? "kanban-search-dropdown-item--active dark:bg-gray-700" : ""}`}
                        onMouseEnter={() => setActiveSearchIndex(idx)}
                        onClick={() => handleSelectSearchLead(l)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{l.name}</div>
                          <div className="text-[11px] text-gray-500 truncate">{l.phone} {l.companyName && `· ${l.companyName}`}</div>
                        </div>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: l.statusColor || "#e2e8f0", color: "#fff" }}>
                          {l.statusName}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {isMobile ? (
            <div>
              {/* Status tabs: tap (or swipe the list left/right) to switch status */}
              <div
                ref={mobileTabsRef}
                role="tablist"
                aria-label="Lead status"
                className="lm-tabs-strip flex gap-2 overflow-x-auto pb-2 mb-1"
              >
                {statusColumns.map((col) => {
                  const active = col.id === activeMobileId;
                  return (
                    <button
                      key={col.id}
                      role="tab"
                      aria-selected={active}
                      data-active={active ? "true" : "false"}
                      onClick={() => setMobileStatusId(col.id)}
                      className={`shrink-0 flex items-center gap-2 px-3.5 min-h-[44px] rounded-full text-sm font-semibold border transition-colors ${
                        active
                          ? "text-white border-transparent shadow-sm"
                          : "bg-white text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700"
                      }`}
                      style={active ? { backgroundColor: col.color } : undefined}
                    >
                      {!active && (
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: col.color }} />
                      )}
                      <span className="whitespace-nowrap">{col.name}</span>
                      <span
                        className={`text-xs font-bold rounded-full px-2 py-0.5 tabular-nums ${
                          active
                            ? "bg-white/90 text-gray-800"
                            : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-200"
                        }`}
                      >
                        {statusCounts[col.id] || 0}
                      </span>
                    </button>
                  );
                })}
              </div>

              {activeMobileColumn && (
                <div onTouchStart={handleSwipeStart} onTouchEnd={handleSwipeEnd}>
                  <Column
                    key={activeMobileColumn.id}
                    mobile
                    column={activeMobileColumn}
                    leadsData={leadsByStatus[activeMobileColumn.id]}
                    apiTotalCount={statusCounts[activeMobileColumn.id] || 0}
                    moveLead={moveLead}
                    accountType={user?.accountType}
                    onCardClick={handleCardClick}
                    onLoadMore={loadMoreForStatus}
                    onOpenHistory={handleOpenHistory}
                    onIvrError={handleIvrError}
                    onVideoCall={handleCreateVideoCall}
                    canVideoCall={canVideoCall}
                    onOpenActions={openActionsMenu}
                    onQuickStatus={openQuickStatus}
                    highlightedLeadId={highlightedLeadId}
                    cardRefs={leadCardRefs}
                  />
                </div>
              )}
            </div>
          ) : (
          <div className="">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-2">
              {statusColumns.map((col) => (
                <Column
                  key={col.id}
                  column={col}
                  leadsData={leadsByStatus[col.id]}
                  apiTotalCount={statusCounts[col.id] || 0}
                  moveLead={moveLead}
                  accountType={user?.accountType}
                  onCardClick={handleCardClick}
                  onLoadMore={loadMoreForStatus}
                  onOpenHistory={handleOpenHistory}
                  onIvrError={handleIvrError}
                  onVideoCall={handleCreateVideoCall}
                  canVideoCall={canVideoCall}
                  onOpenActions={openActionsMenu}
                  highlightedLeadId={
                    highlightedLeadId &&
                    leadsByStatus[col.id]?.leads?.some((l) => l.leadId === highlightedLeadId)
                      ? highlightedLeadId
                      : null
                  }
                  cardRefs={leadCardRefs}
                />
              ))}
            </div>
          </div>
          )}

          {showModal && (
            <div className="fixed inset-0 flex justify-center items-center z-50 bg-black bg-opacity-40 lm-overlay-in">
              <div className="bg-white p-6 rounded-lg shadow-lg lm-modal-pop w-96 dark:bg-gray-900">
                <h3 className="text-lg font-semibold cfg-heading dark:text-white">
                  Reason for status change
                </h3>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full h-24 border rounded p-2 text-sm dark:bg-gray-800 dark:text-white"
                  placeholder="Enter reason..."
                ></textarea>
                <div className="flex justify-end mt-4 gap-2">
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setReason("");
                      setPendingUpdate(null);
                    }}
                    className="px-4 py-2 bg-[#F3F4F6] text-[#374151] rounded hover:bg-[#E5E7EB] text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleModalSubmit}
                    disabled={!reason.trim()}
                    className="px-4 py-2 bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] text-sm disabled:opacity-50"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* New IVR Missing Modal */}
          {showIvrModal && (
            <div className="fixed inset-0 flex justify-center items-center z-[70] bg-black bg-opacity-50 lm-overlay-in">
              <div className="bg-white p-6 rounded-lg shadow-lg lm-modal-pop w-96 dark:bg-gray-800 text-center relative">
                <button
                  onClick={() => setShowIvrModal(false)}
                  className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-white transition"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                  <PhoneCall className="h-7 w-7 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-bold cfg-heading dark:text-white">
                  Service Not Configured
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-5 leading-relaxed">
                  The IVR integration service is not set up on your account. To enable this feature and start making calls, please contact our helpline.
                </p>
                <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg mb-5 border border-gray-100 dark:border-gray-700">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">Support Helpline</p>
                  <a href="tel:+919876543210" className="text-xl font-bold text-[#673AB7] dark:text-[#8B5CF6] hover:underline">
                    +91 98765 43210
                  </a>
                </div>
                <button
                  onClick={() => setShowIvrModal(false)}
                  className="w-full px-4 py-2.5 bg-[#673AB7] text-white rounded-md hover:bg-[#5E35A6] transition font-medium shadow-sm"
                >
                  Got It
                </button>
              </div>
            </div>
          )}

        </div>
        {showLeadModal && selectedLead && (
          <LeadDetailsModal
            lead={selectedLead}
            labels={labels}
            fieldDefinitions={fieldDefinitions}
            onClose={closeLeadModal}
            onOpenCallHistory={handleOpenCallHistory}
            onOpenLeadHistory={handleOpenLeadHistory}
            onOpenActions={openActionsMenu}
            onOpenComment={openCommentModal}
            onInlineEdit={handleInlineEdit}
            onCustomFieldEdit={handleCustomFieldEdit}
            leadNav={leadNav}
          />
        )}

        <CallHistoryModal
          open={showCallHistoryModal}
          lead={callHistoryLead}
          data={callHistoryData}
          loading={callHistoryLoading}
          onLeadUpdated={patchLeadById}
          onClose={() => setShowCallHistoryModal(false)}
        />
        <CommentEditModal
          open={showCommentModal}
          value={commentDraft}
          onChange={setCommentDraft}
          onSave={handleCommentSave}
          onClose={() => setShowCommentModal(false)}
        />
        <LeadHistoryModal
          open={showLeadHistoryModal}
          lead={leadHistoryLead}
          historyData={leadHistoryData}
          loading={leadHistoryLoading}
          onClose={() => setShowLeadHistoryModal(false)}
        />
        <HistoryModal
          open={showHistoryModal}
          lead={historyModalLead}
          activeTab={historyModalTab}
          onTabChange={setHistoryModalTab}
          callData={historyModalCallData}
          callLoading={historyModalCallLoading}
          leadHistoryData={historyModalLeadData}
          leadHistoryLoading={historyModalLeadLoading}
          onLeadUpdated={patchLeadById}
          onClose={() => setShowHistoryModal(false)}
        />
        <VideoCallModal
          open={showVideoCallModal}
          lead={videoCallLead}
          data={videoCallData}
          onClose={() => setShowVideoCallModal(false)}
        />

        <LMActionsMenuModal
          open={showActionsMenu}
          lead={actionLead}
          onClose={closeActionsMenu}
          onSelect={(action) => {
            setShowActionsMenu(false);
            if (action === "status") setShowQuickStatusModal(true);
            if (action === "followup") setShowFollowUpModal(true);
            if (action === "appointment") setShowAppointmentModal(true);
            if (action === "project") setShowLabelModal(true);
            if (action === "sms") handleOpenSmsModal();
            if (action === "whatsapp") handleOpenWhatsAppModal();
            if (action === "email") handleOpenEmailModal();
            if (action === "convert") setShowConvertModal(true);
            if (action === "block") setShowBanModal(true);
          }}
        />
        <LMStatusModal
          open={showQuickStatusModal}
          statuses={statusColumns}
          currentStatus={actionLead?.statusName}
          onConfirm={handleQuickStatusChange}
          onClose={() => setShowQuickStatusModal(false)}
        />
        <LMFollowUpModal
          open={showFollowUpModal}
          users={teamUsers}
          loggedInUser={user}
          onClose={() => setShowFollowUpModal(false)}
          onSubmit={handleFollowUpSubmit}
        />
        <LMAppointmentModal
          open={showAppointmentModal}
          lead={actionLead}
          companyId={companyId}
          currentUserId={user?.id}
          onClose={() => setShowAppointmentModal(false)}
          onScheduled={() => { }}
        />
        <LMLabelModal
          open={showLabelModal}
          labels={labels}
          onClose={() => setShowLabelModal(false)}
          onConfirm={handleAssignProject}
        />
        <LMConvertModal
          open={showConvertModal}
          onClose={() => setShowConvertModal(false)}
          onConfirm={handleConvertLead}
        />
        <LMBanModal
          open={showBanModal}
          onClose={() => setShowBanModal(false)}
          onConfirm={handleBlockLead}
        />
        <LMWhatsAppModal
          open={showWhatsAppModal}
          templates={whatsappTemplates}
          onClose={() => setShowWhatsAppModal(false)}
          onSend={handleSendWhatsApp}
        />
        <LMSmsModal
          open={showSmsModal}
          templates={smsTemplates}
          onClose={() => setShowSmsModal(false)}
          onSend={handleSendSms}
        />
        <LMEmailModal
          open={showEmailModal}
          templates={emailTemplates}
          onClose={() => setShowEmailModal(false)}
          onSend={handleSendEmail}
          sendingEmailId={sendingEmailId}
        />
      </DndProvider>
    </>
  );
}

// ── Comment Edit popup (bada textarea, lamba comment likhne ke liye) ─────
function CommentEditModal({ open, value, onChange, onSave, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 lm-overlay-in">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl lm-modal-pop w-full max-w-md">
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
            💬 Edit Comment
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <textarea
            autoFocus
            rows={6}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm dark:bg-gray-700 dark:text-white outline-none focus:ring-2 focus:ring-[#8B5CF6] resize-none"
            placeholder="Enter comment..."
          />
        </div>

        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#F3F4F6] text-[#374151] dark:bg-gray-600 rounded hover:bg-[#E5E7EB] dark:hover:bg-gray-500 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] text-sm"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

const LeadDetailsModal = memo(function LeadDetailsModal({
  lead,
  labels,
  fieldDefinitions,
  onClose,
  onOpenCallHistory,
  onOpenLeadHistory,
  onOpenActions,
  onOpenComment,
  onInlineEdit,
  onCustomFieldEdit,
  leadNav,
}) {
  const nextFollowUp = lead?.followUps?.[0];
  const scrollRef = useRef(null);

  // when Prev/Next switches lead, start again from the top
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [lead?.leadId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 lm-overlay-in">
      <div ref={scrollRef} className="bg-white dark:bg-gray-800 rounded-lg shadow-xl lm-modal-pop w-full max-w-3xl max-h-[90vh] custom-scroll overflow-y-auto lm-scroll overscroll-contain">
        <div className="sticky top-0 bg-white dark:bg-gray-800 p-3 md:p-4 border-b dark:border-gray-700 flex flex-wrap md:flex-nowrap gap-2 justify-between items-center z-10">
          <h3 className="text-lg md:text-xl font-bold cfg-heading dark:text-white flex-1 md:flex-none">
            Lead Details
          </h3>
          {/* phone: close button sits next to the title */}
          <button
            onClick={() => onClose()}
            aria-label="Close"
            className="md:hidden p-2 -mr-1 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 flex-wrap justify-start md:justify-end w-full md:w-auto">
            <button
              onClick={() => onOpenCallHistory(lead)}
              className="flex items-center gap-1 px-3 py-2 md:py-1.5 text-xs font-semibold rounded-full bg-[#F3E8FF] text-[#673AB7] hover:bg-[#E9D5FF] dark:bg-purple-900 dark:text-purple-200"
            >
              <PhoneCall className="w-3.5 h-3.5" /> Call History
            </button>
            <button
              onClick={() => onOpenLeadHistory(lead)}
              className="flex items-center gap-1 px-3 py-2 md:py-1.5 text-xs font-semibold rounded-full bg-[#F3E8FF] text-[#673AB7] hover:bg-[#E9D5FF] dark:bg-indigo-900 dark:text-indigo-200"
            >
              <History className="w-3.5 h-3.5" /> Lead History
            </button>
            <button
              onClick={() => onOpenActions(lead)}
              className="flex items-center gap-1 px-3 py-2 md:py-1.5 text-xs font-semibold rounded-full bg-[#E0F2FE] text-[#075985] hover:bg-[#BAE6FD] dark:bg-sky-900 dark:text-sky-200"
            >
              <MoreVertical className="w-3.5 h-3.5" /> Quick Actions
            </button>
            <button
              onClick={() => onClose()}
              className="hidden md:block text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-3 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-3">
                Basic Information
              </h4>
              <div className="space-y-3">
                <DetailItem
                  label="Name"
                  value={lead.name}
                  icon="👤"
                  field="name"
                  onSave={onInlineEdit}
                />

                <DetailItem
                  label="Phone"
                  value={
                    <a
                      href={googleExactSearchUrl(lead.phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#673AB7] hover:underline cursor-pointer"
                      title="Search this number on Google"
                    >
                      {lead.phone}
                    </a>
                  }
                  icon="📞"
                />

                <DetailItem
                  label="Email"
                  value={lead.email}
                  icon="✉️"
                  field="email"
                  onSave={onInlineEdit}
                  searchValue={lead.email}
                />
                <DetailItem
                  label="Company"
                  value={lead.companyName}
                  icon="🏢"
                  field="companyName"
                  onSave={onInlineEdit}
                />
                <DetailItem
                  label="Reference"
                  value={lead.reference}
                  icon="🔗"
                />
                <DetailItem
                  label="Address"
                  value={lead.address}
                  icon="🏘️"
                  field="address"
                  onSave={onInlineEdit}
                />
                <div className="flex items-start">
                  <span className="mr-2 mt-0.5">💬</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Comment</p>
                    <p
                      onClick={onOpenComment}
                      className="text-sm font-medium dark:text-white break-words line-clamp-2 cursor-pointer hover:underline"
                      title="Click to edit comment"
                    >
                      {lead.comment ? (
                        lead.comment
                      ) : (
                        <span className="italic text-gray-400">Add Comment</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-3">
                Status Information
              </h4>
              <div className="space-y-3">
                <DetailItem
                  label="Status"
                  value={lead.statusName}
                  icon="🏷️"
                  badgeColor={lead.statusColor}
                />
                <DetailItem
                  label="Project"
                  value={lead.projectName}
                  icon="📋"
                  field="projectName"
                  onSave={onInlineEdit}
                  options={labels}
                />
                <DetailItem
                  label="Reason"
                  value={lead.reason}
                  icon="🗒️"
                />
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-3">
                Additional Details
              </h4>
              <div className="space-y-3">
                <DetailItem
                  label="Source"
                  value={lead.sourceName}
                  icon="🔗"
                  field="sourceName"
                  onSave={onInlineEdit}
                />
                <DetailItem
                  label="City"
                  value={lead.city}
                  icon="📍"
                  field="city"
                  onSave={onInlineEdit}
                />
                <DetailItem
                  label="State"
                  value={lead.state}
                  icon="📍"
                  field="state"
                  onSave={onInlineEdit}
                />
                <DetailItem
                  label="Alt. contact"
                  value={lead.otherContactNo}
                  icon="📱"
                  field="otherContactNo"
                  onSave={onInlineEdit}
                />
                {lead.leadDate && (
                  <DetailItem
                    label="Lead date"
                    value={new Date(lead.leadDate).toLocaleDateString("en-IN")}
                    icon="📆"
                  />
                )}
                <DetailItem
                  label="Converted"
                  value={lead.isConvertedLead ? "Yes" : "No"}
                  icon="✅"
                />
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-3">
                Assignment
              </h4>
              <div className="space-y-3">
                <DetailItem
                  label="Created By"
                  value={lead.createdByName}
                  icon="👨‍💼"
                />
                <DetailItem
                  label="Assigned To"
                  value={lead.assignedToName}
                  icon="👩‍💼"
                />

                <DetailItem
                  label="Created At"
                  value={new Date(lead.createdAt)
                    .toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })
                    .replace(",", "")}
                  icon="⏰"
                />

                <DetailItem
                  label="Updated At"
                  value={new Date(lead.updatedAt)
                    .toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })
                    .replace(",", "")}
                  icon="🔄"
                />

                {nextFollowUp?.nextFollowUpDate && (
                  <DetailItem
                    label="Next follow-up"
                    value={`${nextFollowUp.nextFollowUpDate}${nextFollowUp.nextFollowUpTime ? " " + nextFollowUp.nextFollowUpTime.substring(0, 5) : ""}`}
                    icon="⏰"
                  />
                )}

                {nextFollowUp?.lastFollowUpDate && (
                  <DetailItem
                    label="Last follow-up"
                    value={new Date(nextFollowUp.lastFollowUpDate).toLocaleDateString("en-IN")}
                    icon="🕓"
                  />
                )}
              </div>
            </div>

            {lead.followUps?.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-3">
                  Follow-ups
                </h4>
                <div className="space-y-3 max-h-60 overflow-y-auto lm-scroll">
                  {lead.followUps.map((followUp, index) => (
                    <div
                      key={followUp.id || index}
                      className="border-b dark:border-gray-600 pb-3 last:border-0"
                    >
                      <div className="flex items-start">
                        <span className="mr-2 mt-0.5">📅</span>
                        <div>
                          <p className="text-sm font-medium dark:text-white">
                            {new Date(
                              `${followUp.nextFollowUpDate}T${followUp.nextFollowUpTime}`
                            )
                              .toLocaleString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })
                              .replace(",", "")}
                          </p>

                          <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                            <span className="font-medium">Notes:</span>{" "}
                            {followUp.notes || "No notes"}
                          </p>
                          {followUp.followedByName && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Followed by: {followUp.followedByName}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* --- SECTION: CUSTOM FIELDS (Dynamically Generated) --- */}
          {fieldDefinitions && fieldDefinitions.length > 0 && (
            <div className="md:col-span-2 space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
                  <span>⚙️</span> Additional Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {fieldDefinitions
                    .filter((def) => def.active !== false)
                    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                    .map((def) => {
                      const val = lead?.customFields?.[def.fieldKey] ?? lead?.[def.fieldKey];
                      let parsedOptions = [];
                      if (def.options) {
                        if (Array.isArray(def.options)) {
                          parsedOptions = def.options;
                        } else if (typeof def.options === 'string') {
                          parsedOptions = def.options.split(',').map(o => o.trim());
                        }
                      }
                      return (
                        <DetailItem
                          key={def.id || def.fieldKey}
                          label={def.label || def.fieldKey}
                          value={val}
                          icon="📌"
                          field={def.fieldKey}
                          onSave={onCustomFieldEdit}
                          options={parsedOptions.length > 0 ? parsedOptions : undefined}
                        />
                      );
                    })}
                </div>
              </div>
            </div>
          )}

        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 p-3 md:p-4 border-t dark:border-gray-700 flex items-center justify-between md:justify-end gap-2">
          {/* phone only: jump to the previous / next lead in this status without closing */}
          {leadNav && leadNav.total > 1 && (
            <div className="flex md:hidden items-center gap-2">
              <button
                onClick={leadNav.onPrev || undefined}
                disabled={!leadNav.onPrev}
                className="flex items-center gap-1 px-3 min-h-[44px] rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                {leadNav.index + 1} / {leadNav.total}
              </span>
              <button
                onClick={leadNav.onNext || undefined}
                disabled={!leadNav.onNext}
                className="flex items-center gap-1 px-3 min-h-[44px] rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 disabled:opacity-40"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          <button
            onClick={() => onClose()}
            className="px-4 py-2 min-h-[44px] md:min-h-0 bg-[#673AB7] hover:bg-[#5E35A6] text-white rounded-md transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
});

const DetailItemInner = ({ label, value, icon, badgeColor, field, onSave, options, searchValue }) => {
  const isEditable = !!(field && onSave);

  if (!value && !isEditable) return null;

  return (
    <div className="flex items-start">
      <span className="mr-2 mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        {badgeColor ? (
          <span
            className="text-sm font-medium dark:text-white px-2 py-0.5 rounded-full"
            style={{ backgroundColor: badgeColor }}
          >
            {value}
          </span>
        ) : isEditable ? (
          <p className="text-sm font-medium dark:text-white break-all">
            <EditableField
              value={value}
              onSave={(v) => onSave(field, v)}
              label={label}
              options={options}
            />
            {searchValue && (
              <a
                href={googleExactSearchUrl(searchValue)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex align-middle ml-2 text-gray-400 hover:text-[#673AB7]"
                title="Search this on Google"
              >
                <Search className="w-3.5 h-3.5" />
              </a>
            )}
          </p>
        ) : (
          <p className="text-sm font-medium dark:text-white break-all">
            {value}
          </p>
        )}
      </div>
    </div>
  );
};

const DetailItem = memo(DetailItemInner);

// ── Call History popup ────────────────────────────────────────────────────
function CallHistoryModal({ open, lead, data, loading, onClose, onLeadUpdated }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 lm-overlay-in">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl lm-modal-pop w-full max-w-lg max-h-[85vh] overflow-y-auto lm-scroll overscroll-contain">
        <div className="sticky top-0 bg-white dark:bg-gray-800 p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-bold cfg-heading dark:text-white flex items-center gap-2">
            <PhoneCall className="w-4 h-4 text-[#673AB7]" /> Call History
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {lead && (
          <div className="flex items-center gap-3 px-4 py-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <div className="w-9 h-9 rounded-full bg-[#F3E8FF] text-[#673AB7] flex items-center justify-center font-bold text-sm">
              {(lead.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{lead.name}</p>
              {lead.phone && <p className="text-xs text-gray-500 dark:text-gray-300">📞 {lead.phone}</p>}
            </div>
          </div>
        )}

        <div className="p-4 space-y-3">
          <CallRecordingsList lead={lead} data={data} loading={loading} onLeadUpdated={onLeadUpdated} />
        </div>
      </div>
    </div>
  );
}

// ── Lead History popup ────────────────────────────────────────────────────
function renderHistoryChange(item, idx) {
  if (item.changeType === "lead created") {
    return (
      <div key={idx} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0"></span>
        Lead was created
      </div>
    );
  }

  if (item.changeType && item.changeType.includes("UPDATED_FIELDS")) {
    const changeText = item.changeType.replace("UPDATED_FIELDS: ", "");
    const changes = [];
    const fieldRegex = /([^:]+?)\s*\[From:\s*([^,]+),\s*To:\s*([^\]]+)\]/g;
    let match;
    while ((match = fieldRegex.exec(changeText)) !== null) {
      const field = match[1].trim();
      const from = match[2].trim() || "Empty";
      const to = match[3].trim();
      if (field.toLowerCase().includes("color")) continue;
      changes.push({ field, from, to });
    }

    if (changes.length === 0) {
      const filteredLines = changeText
        .split(",")
        .filter((line) => !line.toLowerCase().includes("color"));
      return filteredLines.map((line, i) => (
        <div key={i} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
          <span className="w-1.5 h-1.5 rounded-full bg-[#8B5CF6] shrink-0"></span>
          {line.trim()}
        </div>
      ));
    }

    return changes.map((c, i) => (
      <div key={i} className="mb-2 last:mb-0">
        <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">
          {c.field}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] px-2 py-0.5 rounded bg-red-50 text-red-600 font-semibold">{c.from}</span>
          <span className="text-gray-400 text-xs">→</span>
          <span className="text-[11px] px-2 py-0.5 rounded bg-green-50 text-green-600 font-semibold">{c.to}</span>
        </div>
      </div>
    ));
  }

  return (
    <div key={idx} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
      <span className="w-1.5 h-1.5 rounded-full bg-[#8B5CF6] shrink-0"></span>
      {item.changeType}
    </div>
  );
}

function LeadHistoryModal({ open, lead, historyData, loading, onClose }) {
  if (!open) return null;

  const sorted = [...(historyData || [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 lm-overlay-in">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl lm-modal-pop w-full max-w-lg max-h-[85vh] overflow-y-auto lm-scroll overscroll-contain">
        <div className="sticky top-0 bg-white dark:bg-gray-800 p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-bold cfg-heading dark:text-white flex items-center gap-2">
            <History className="w-4 h-4 text-[#673AB7]" /> Lead History
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {lead && (
          <div className="flex items-center gap-3 px-4 py-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <div className="w-9 h-9 rounded-full bg-[#F3E8FF] text-[#673AB7] flex items-center justify-center font-bold text-sm">
              {(lead.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{lead.name}</p>
              <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-300">
                {lead.phone && <span>📞 {lead.phone}</span>}
                {lead.email && <span>✉️ {lead.email}</span>}
                {lead.companyName && <span>🏢 {lead.companyName}</span>}
              </div>
            </div>
          </div>
        )}

        <div className="p-4 space-y-3">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-b-4 border-[#673AB7]"></div>
            </div>
          ) : sorted.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-6">No history available for this lead.</p>
          ) : (
            sorted.map((item, idx) => (
              <div
                key={item.historyId || idx}
                className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-[#F3E8FF] text-[#673AB7] flex items-center justify-center text-xs font-bold shrink-0">
                    {(item.changedByName || "U").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-800 dark:text-white">
                      {item.changedByName || "Unknown User"}
                    </p>
                    <p className="text-[11px] text-gray-400">{formatDateTime(item.createdAt)}</p>
                  </div>
                </div>
                <div className="border-l-2 border-[#8B5CF6] pl-3 ml-1 space-y-1.5">
                  {renderHistoryChange(item, idx)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function HistoryModal({
  open,
  lead,
  activeTab,
  onTabChange,
  callData,
  callLoading,
  leadHistoryData,
  leadHistoryLoading,
  onClose,
  onLeadUpdated,
}) {
  if (!open) return null;

  const sortedLeadHistory = [...(leadHistoryData || [])].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 lm-overlay-in">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl lm-modal-pop w-full max-w-lg max-h-[85vh] overflow-y-auto lm-scroll overscroll-contain">
        <div className="sticky top-0 bg-white dark:bg-gray-800 p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-bold cfg-heading dark:text-white flex items-center gap-2">
            <History className="w-4 h-4 text-[#673AB7]" /> History
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {lead && (
          <div className="flex items-center gap-3 px-4 py-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <div className="w-9 h-9 rounded-full bg-[#F3E8FF] text-[#673AB7] flex items-center justify-center font-bold text-sm">
              {(lead.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{lead.name}</p>
              {lead.phone && <p className="text-xs text-gray-500 dark:text-gray-300">📞 {lead.phone}</p>}
            </div>
          </div>
        )}

        <div className="flex border-b dark:border-gray-700 px-4 pt-2 gap-2 sticky top-[65px] bg-white dark:bg-gray-800 z-[1]">
          <button
            onClick={() => onTabChange("call")}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-t-md transition-colors ${activeTab === "call"
                ? "text-[#673AB7] border-b-2 border-[#673AB7]"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              }`}
          >
            <PhoneCall className="w-3.5 h-3.5" /> Call History
          </button>
          <button
            onClick={() => onTabChange("lead")}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-t-md transition-colors ${activeTab === "lead"
                ? "text-[#673AB7] border-b-2 border-[#673AB7]"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              }`}
          >
            <History className="w-3.5 h-3.5" /> Lead History
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* Kept mounted (just hidden) so transcripts survive tab switches */}
          <div className={activeTab === "call" ? "space-y-3" : "hidden"}>
            <CallRecordingsList lead={lead} data={callData} loading={callLoading} onLeadUpdated={onLeadUpdated} />
          </div>
          {activeTab === "call" ? null : leadHistoryLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-b-4 border-[#673AB7]"></div>
            </div>
          ) : sortedLeadHistory.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-6">No history available for this lead.</p>
          ) : (
            sortedLeadHistory.map((item, idx) => (
              <div
                key={item.historyId || idx}
                className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-[#F3E8FF] text-[#673AB7] flex items-center justify-center text-xs font-bold shrink-0">
                    {(item.changedByName || "U").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-800 dark:text-white">
                      {item.changedByName || "Unknown User"}
                    </p>
                    <p className="text-[11px] text-gray-400">{formatDateTime(item.createdAt)}</p>
                  </div>
                </div>
                <div className="border-l-2 border-[#8B5CF6] pl-3 ml-1 space-y-1.5">
                  {renderHistoryChange(item, idx)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function VideoCallModal({ open, lead, data, onClose }) {
  const [copied, setCopied] = useState("");
  const [sendingWA, setSendingWA] = useState(false);
  const [sendingSMS, setSendingSMS] = useState(false);

  if (!open) return null;

  const handleCopy = async (text, which) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(""), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
      toast.error("Failed to copy link.");
    }
  };

  const handleSendLink = async (channel) => {
    if (!lead?.phone) {
      toast.error("Lead has no phone number");
      return;
    }

    // Assuming the API returns the room id as data.roomId or data.id
    const roomId = data?.roomId || data?.id;
    if (!roomId) {
      toast.error("Video call room ID is missing.");
      return;
    }

    if (channel === "WHATSAPP") setSendingWA(true);
    else setSendingSMS(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/videocalls/${roomId}/send-link`,
        {
          channel,
          phone: lead.phone,
          link: data?.link,
          hostLink: data?.hostLink,
          password: data?.password || "",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.status === "sent" || response.status === 200) {
        toast.success(`Link sent successfully via ${channel === "WHATSAPP" ? "WhatsApp" : "SMS"}!`);
      }
    } catch (err) {
      console.error(`Error sending link via ${channel}:`, err);
      const apiError = err.response?.data?.error;
      const apiMsg = err.response?.data?.message;
      const fallback = "Failed to send link. Please try again.";
      toast.error(apiError || apiMsg || fallback);
    } finally {
      if (channel === "WHATSAPP") setSendingWA(false);
      else setSendingSMS(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 lm-overlay-in">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl lm-modal-pop w-full max-w-md">
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-bold cfg-heading dark:text-white flex items-center gap-2">
            <Video className="w-5 h-5 text-[#673AB7]" /> Video Call
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {lead && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Video call room created for{" "}
              <b className="text-gray-800 dark:text-white">{lead.name}</b>.
            </p>
          )}

          {data?.requiresPassword && data?.password && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide mb-1">
                Room Password
              </p>
              <p className="text-sm font-bold text-amber-800 dark:text-amber-200">{data.password}</p>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              Link for lead
            </p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={data?.link || ""}
                onFocus={(e) => e.target.select()}
                className="flex-1 text-xs border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white truncate"
              />
              <button
                onClick={() => handleCopy(data?.link, "link")}
                className="px-3 py-2 bg-[#673AB7] text-white text-xs rounded hover:bg-[#5E35A6] shrink-0"
              >
                {copied === "link" ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <a
              href={data?.hostLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 text-center px-4 py-2 bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] text-sm font-medium"
            >
              Join as Host
            </a>
            <button
              onClick={() => handleCopy(data?.hostLink, "host")}
              className="px-4 py-2 bg-[#F3F4F6] text-[#374151] dark:bg-gray-600 dark:text-white rounded hover:bg-[#E5E7EB] dark:hover:bg-gray-500 text-sm"
            >
              {copied === "host" ? "Copied!" : "Copy host link"}
            </button>
          </div>

          <div className="pt-4 border-t dark:border-gray-700">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Share link directly
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handleSendLink("WHATSAPP")}
                disabled={sendingWA}
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-[#25D366] text-white rounded hover:bg-[#20bd5a] text-sm font-medium transition-colors disabled:opacity-70"
              >
                {sendingWA ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.888-.788-1.487-1.761-1.663-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                )}
                WhatsApp
              </button>

              <button
                onClick={() => handleSendLink("SMS")}
                disabled={sendingSMS}
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-[#3B82F6] text-white rounded hover:bg-[#2563eb] text-sm font-medium transition-colors disabled:opacity-70"
              >
                {sendingSMS ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <MessageSquare className="w-4 h-4" />
                )}
                SMS
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 border-t dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#F3F4F6] text-[#374151] dark:bg-gray-600 dark:text-white rounded hover:bg-[#E5E7EB] dark:hover:bg-gray-500 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function LMActionsMenuModal({ open, lead, onClose, onSelect }) {
  if (!open || !lead) return null;

  const items = [
    { key: "status", label: "Change Status", icon: <RefreshCw className="w-4 h-4" />, color: "#3b82f6" },
    { key: "followup", label: "Add Follow-up", icon: <CalendarPlus className="w-4 h-4" />, color: "#8B5CF6" },
    { key: "appointment", label: "Appointment", icon: <CalendarCheck className="w-4 h-4" />, color: "#0EA5E9" },
    { key: "project", label: "Assign Project", icon: <Tag className="w-4 h-4" />, color: "#F59E0B" },
    { key: "sms", label: "Send SMS", icon: <MessageSquare className="w-4 h-4" />, color: "#2563eb" },
    { key: "whatsapp", label: "Send WhatsApp", icon: <LeadsWhatsAppIcon />, color: "#16a34a" },
    { key: "email", label: "Send Email", icon: <Mail className="w-4 h-4" />, color: "#dc2626" },
    {
      key: "convert",
      label: "Convert to Customer",
      icon: <CheckCircle2 className="w-4 h-4" />,
      color: "#16a34a",
      disabled: lead.isConvertedLead,
    },
    { key: "block", label: "Block Lead", icon: <Ban className="w-4 h-4" />, color: "#dc2626" },
  ];

  return (
    <div className="fixed inset-0 flex justify-center items-center z-[65] bg-black bg-opacity-40 lm-overlay-in">
      <div className="bg-white p-5 rounded-lg shadow-lg lm-modal-pop w-full max-w-sm dark:bg-gray-900">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-semibold cfg-heading dark:text-white truncate pr-2">
            Quick Actions{lead?.name ? ` · ${lead.name}` : ""}
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-white shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-2 gap-2">
          {items.map((item) => (
            <button
              key={item.key}
              disabled={item.disabled}
              onClick={() => onSelect(item.key)}
              className="flex flex-col items-center justify-center gap-1.5 p-3 min-h-[88px] md:min-h-0 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span
                className="flex items-center justify-center w-8 h-8 rounded-full text-white"
                style={{ backgroundColor: item.color }}
              >
                {item.icon}
              </span>
              <span className="text-xs md:text-[11px] font-medium text-gray-700 dark:text-gray-200 text-center leading-tight">
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function LMStatusModal({ open, statuses, currentStatus, onConfirm, onClose }) {
  const [selected, setSelected] = useState(currentStatus || "");
  const [reason, setReason] = useState("");

  useEffect(() => {
    setSelected(currentStatus || "");
    setReason("");
  }, [currentStatus, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex justify-center items-center z-[65] bg-black bg-opacity-40 lm-overlay-in">
      <div className="bg-white p-6 rounded-lg shadow-lg lm-modal-pop w-96 dark:bg-gray-900">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold cfg-heading dark:text-white">Change Status</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">New status</label>
        <select
          className="w-full border rounded p-2 text-sm mt-1 mb-3 dark:bg-gray-800 dark:text-white dark:border-gray-700"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          <option value="">— select —</option>
          {(statuses || []).map((s) => (
            <option key={s.id || s.name} value={s.name}>{s.name}</option>
          ))}
        </select>
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Reason</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full h-20 border rounded p-2 text-sm mt-1 dark:bg-gray-800 dark:text-white dark:border-gray-700"
          placeholder="Enter reason..."
        />
        <div className="flex justify-end mt-4 gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-[#F3F4F6] text-[#374151] rounded hover:bg-[#E5E7EB] text-sm">Cancel</button>
          <button
            onClick={() => onConfirm(selected, reason)}
            disabled={!selected || !reason.trim()}
            className="px-4 py-2 bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] text-sm disabled:opacity-50"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function LMFollowUpModal({ open, users, loggedInUser, onClose, onSubmit }) {
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [followedById, setFollowedById] = useState("");

  const isExecutive = loggedInUser?.accountType === "EXECUTIVE";
  const selfUser = (users || []).find((u) => u.id === loggedInUser?.id);

  useEffect(() => {
    if (open) {
      setFollowedById(isExecutive ? (loggedInUser?.id || "") : "");
      setNotes("");
      setDate("");
      setTime("");
    }
  }, [open, isExecutive, loggedInUser]);

  if (!open) return null;

  const handleSubmit = () => {
    if (!date || !time) {
      toast.warning("Please pick a follow-up date and time.");
      return;
    }
    onSubmit({ notes, nextFollowUpDate: date, nextFollowUpTime: time, followedById });
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center z-[65] bg-black bg-opacity-40 lm-overlay-in">
      <div className="bg-white p-6 rounded-lg shadow-lg lm-modal-pop w-96 dark:bg-gray-900">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold cfg-heading dark:text-white">Add Follow-up</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full h-16 border rounded p-2 text-sm mt-1 mb-3 dark:bg-gray-800 dark:text-white dark:border-gray-700"
          placeholder="Enter notes..."
        />
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Date</label>
            <input
              type="date"
              value={date}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) => setDate(e.target.value)}
              className="w-full border rounded p-2 text-sm mt-1 dark:bg-gray-800 dark:text-white dark:border-gray-700"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Time</label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full border rounded p-2 text-sm mt-1 dark:bg-gray-800 dark:text-white dark:border-gray-700"
            />
          </div>
        </div>
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Followed by</label>
        <select
          className="w-full border rounded p-2 text-sm mt-1 dark:bg-gray-800 dark:text-white dark:border-gray-700"
          value={followedById}
          onChange={(e) => setFollowedById(e.target.value)}
          disabled={isExecutive}
        >
          {isExecutive ? (
            <option value={loggedInUser?.id || ""}>{selfUser ? `${selfUser.fname} ${selfUser.lname}` : "Me"}</option>
          ) : (
            <>
              <option value="">— select user —</option>
              {(users || []).map((u) => <option key={u.id} value={u.id}>{u.fname} {u.lname}</option>)}
            </>
          )}
        </select>
        <div className="flex justify-end mt-4 gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-[#F3F4F6] text-[#374151] rounded hover:bg-[#E5E7EB] text-sm">Cancel</button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] text-sm">Submit</button>
        </div>
      </div>
    </div>
  );
}

function LMLabelModal({ open, labels, onClose, onConfirm }) {
  const [labelId, setLabelId] = useState("");
  useEffect(() => { if (open) setLabelId(""); }, [open]);
  if (!open) return null;

  const handleConfirm = () => {
    const label = (labels || []).find((l) => String(l.id) === String(labelId));
    onConfirm(labelId, label?.name);
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center z-[65] bg-black bg-opacity-40 lm-overlay-in">
      <div className="bg-white p-6 rounded-lg shadow-lg lm-modal-pop w-96 dark:bg-gray-900">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold cfg-heading dark:text-white">Assign Project</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Project name</label>
        <select
          className="w-full border rounded p-2 text-sm mt-1 dark:bg-gray-800 dark:text-white dark:border-gray-700"
          value={labelId}
          onChange={(e) => setLabelId(e.target.value)}
        >
          <option value="">— select project —</option>
          {(labels || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <div className="flex justify-end mt-4 gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-[#F3F4F6] text-[#374151] rounded hover:bg-[#E5E7EB] text-sm">Cancel</button>
          <button onClick={handleConfirm} disabled={!labelId} className="px-4 py-2 bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] text-sm disabled:opacity-50">Assign</button>
        </div>
      </div>
    </div>
  );
}

function LMConvertModal({ open, onClose, onConfirm }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 flex justify-center items-center z-[65] bg-black bg-opacity-40 lm-overlay-in">
      <div className="bg-white p-6 rounded-lg shadow-lg lm-modal-pop w-96 dark:bg-gray-900">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold cfg-heading dark:text-white">Convert to Customer</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">Are you sure you want to convert this lead to a customer?</p>
        <div className="flex justify-end mt-4 gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-[#F3F4F6] text-[#374151] rounded hover:bg-[#E5E7EB] text-sm">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm">Confirm</button>
        </div>
      </div>
    </div>
  );
}

function LMBanModal({ open, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 flex justify-center items-center z-[65] bg-black bg-opacity-40 lm-overlay-in">
      <div className="bg-white p-6 rounded-lg shadow-lg lm-modal-pop w-96 dark:bg-gray-900">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold cfg-heading dark:text-white">Block Lead</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Reason</label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full border rounded p-2 text-sm mt-1 dark:bg-gray-800 dark:text-white dark:border-gray-700"
          placeholder="Enter reason..."
        />
        <div className="flex justify-end mt-4 gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-[#F3F4F6] text-[#374151] rounded hover:bg-[#E5E7EB] text-sm">Cancel</button>
          <button onClick={() => onConfirm(reason)} disabled={!reason.trim()} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm disabled:opacity-50">Block</button>
        </div>
      </div>
    </div>
  );
}

function LMWhatsAppModal({ open, templates, onClose, onSend }) {
  const [selected, setSelected] = useState("");
  const [templateDetails, setTemplateDetails] = useState(null);
  const [variableValues, setVariableValues] = useState({});

  useEffect(() => {
    if (!open) {
      setSelected("");
      setTemplateDetails(null);
      setVariableValues({});
    }
  }, [open]);

  const handleChange = (name) => {
    setSelected(name);
    const tpl = (templates || []).find((t) => t.name === name);
    setTemplateDetails(tpl || null);
    setVariableValues({});
  };

  const handleVarChange = (key, val) => setVariableValues((prev) => ({ ...prev, [key]: val }));

  const getPreviewText = (text) => {
    if (!text) return "";
    let updated = text;
    Object.keys(variableValues).forEach((key) => {
      const regex = new RegExp(`{{${key}}}`, "g");
      updated = updated.replace(regex, variableValues[key] || `{{${key}}}`);
    });
    return updated;
  };

  const extractVariables = (text) => {
    if (!text) return [];
    const matches = text.match(/{{(\d+)}}/g);
    return matches ? [...new Set(matches.map((m) => m.match(/\d+/)[0]))].sort() : [];
  };

  if (!open) return null;

  const vars = extractVariables(
    templateDetails?.components?.find((c) => c.type === "BODY")?.text || templateDetails?.body
  );

  return (
    <div className="fixed inset-0 flex justify-center items-center z-[65] bg-black bg-opacity-40 lm-overlay-in">
      <div className="bg-white p-6 rounded-lg shadow-lg lm-modal-pop w-full max-w-md max-h-[85vh] overflow-y-auto lm-scroll overscroll-contain dark:bg-gray-900">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold cfg-heading dark:text-white">Send WhatsApp</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Template</label>
        <select
          className="w-full border rounded p-2 text-sm mt-1 mb-3 dark:bg-gray-800 dark:text-white dark:border-gray-700"
          value={selected}
          onChange={(e) => handleChange(e.target.value)}
        >
          <option value="">— select template —</option>
          {Array.isArray(templates) && templates.map((t) => (
            <option key={t.id || t.name} value={t.name}>{t.name}</option>
          ))}
        </select>

        {templateDetails && (
          <div className="mb-3">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm border border-green-200 dark:border-green-800 mb-3 whitespace-pre-wrap text-green-800 dark:text-green-200">
              {getPreviewText(templateDetails.components?.find((c) => c.type === "BODY")?.text || templateDetails.body)}
            </div>
            {vars.length > 0 && (
              <div className="border-t border-gray-100 dark:border-gray-700 pt-2">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 block">Fill Variables</label>
                {vars.map((v) => (
                  <div key={v} className="flex items-center gap-2 mb-1.5">
                    <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 min-w-[40px]">Var {v}:</span>
                    <input
                      className="flex-1 border rounded p-1.5 text-sm dark:bg-gray-800 dark:text-white dark:border-gray-700"
                      placeholder={`Value for {{${v}}}`}
                      value={variableValues[v] || ""}
                      onChange={(e) => handleVarChange(v, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end mt-4 gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-[#F3F4F6] text-[#374151] rounded hover:bg-[#E5E7EB] text-sm">Cancel</button>
          <button
            onClick={() => onSend(selected, variableValues)}
            disabled={!selected}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

function LMSmsModal({ open, templates, onClose, onSend }) {
  const [page, setPage] = useState(0);
  const itemsPerPage = 8;
  useEffect(() => { setPage(0); }, [open]);
  if (!open) return null;

  const list = templates || [];
  const totalPages = Math.max(1, Math.ceil(list.length / itemsPerPage));
  const currentTemplates = list.slice(page * itemsPerPage, (page + 1) * itemsPerPage);

  return (
    <div className="fixed inset-0 flex justify-center items-center z-[65] bg-black bg-opacity-40 lm-overlay-in">
      <div className="bg-white p-6 rounded-lg shadow-lg lm-modal-pop w-full max-w-lg max-h-[85vh] overflow-y-auto lm-scroll overscroll-contain dark:bg-gray-900">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold cfg-heading dark:text-white">Select SMS Template</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        {list.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-6">No SMS templates found.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              {currentTemplates.map((t) => (
                <button
                  key={t.messageId}
                  onClick={() => onSend(t.messageId, [])}
                  className="text-left border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                >
                  <div className="text-xs font-bold text-gray-700 dark:text-gray-200 mb-1 truncate">
                    {t.templateType || t.templateName || t.messageId}
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-3">{t.templateText}</div>
                </button>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4 text-xs">
                <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 bg-[#673AB7] text-white rounded disabled:opacity-40">Prev</button>
                <span className="text-gray-600 dark:text-gray-300">Page {page + 1} of {totalPages}</span>
                <button disabled={page === totalPages - 1} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 bg-[#673AB7] text-white rounded disabled:opacity-40">Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function LMEmailModal({ open, templates, onClose, onSend, sendingEmailId }) {
  const [page, setPage] = useState(0);
  const itemsPerPage = 8;
  useEffect(() => { setPage(0); }, [open]);
  if (!open) return null;

  const list = templates || [];
  const totalPages = Math.max(1, Math.ceil(list.length / itemsPerPage));
  const currentTemplates = list.slice(page * itemsPerPage, (page + 1) * itemsPerPage);

  return (
    <div className="fixed inset-0 flex justify-center items-center z-[65] bg-black bg-opacity-40 lm-overlay-in">
      <div className="bg-white p-6 rounded-lg shadow-lg lm-modal-pop w-full max-w-lg max-h-[85vh] overflow-y-auto lm-scroll overscroll-contain dark:bg-gray-900">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold cfg-heading dark:text-white">Select Email Template</h3>
          <button onClick={onClose} disabled={sendingEmailId !== null} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-white disabled:opacity-40">
            <X className="w-5 h-5" />
          </button>
        </div>
        {list.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-6">No Email templates found.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              {currentTemplates.map((t) => {
                const tId = t.emailTemplateId || t.id;
                const isSending = sendingEmailId === tId;
                return (
                  <button
                    key={tId}
                    onClick={() => !isSending && onSend(tId, [])}
                    disabled={isSending}
                    className="relative text-left border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all disabled:cursor-not-allowed"
                  >
                    {isSending && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-gray-900/60 rounded-lg">
                        <div className="w-5 h-5 border-2 border-[#673AB7] border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    <div className="text-xs font-bold text-gray-700 dark:text-gray-200 mb-1 truncate">{t.title || t.subject}</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{t.subject || "No Subject"}</div>
                  </button>
                );
              })}
            </div>
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4 text-xs">
                <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 bg-[#673AB7] text-white rounded disabled:opacity-40">Prev</button>
                <span className="text-gray-600 dark:text-gray-300">Page {page + 1} of {totalPages}</span>
                <button disabled={page === totalPages - 1} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 bg-[#673AB7] text-white rounded disabled:opacity-40">Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function LMAppointmentModal({ open, lead, companyId, currentUserId, onClose, onScheduled }) {
  const [phase, setPhase] = useState("loading"); // loading | history | form
  const [appointments, setAppointments] = useState([]);

  const [departments, setDepartments] = useState([]);
  const [persons, setPersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);

  const [departmentId, setDepartmentId] = useState("");
  const [personId, setPersonId] = useState("");
  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("SCHEDULED");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !lead?.leadId) return;
    setPhase("loading");
    setAppointments([]);
    setDepartmentId("");
    setPersonId("");
    setPersons([]);
    setPreferredDate("");
    setPreferredTime("");
    setNotes("");
    setStatus("SCHEDULED");

    const load = async () => {
      try {
        const [aptRes, deptRes] = await Promise.all([
          axios.get(`${API_URL}/api/appointments/lead/${lead.leadId}`, { headers: lmAuthHeader() }),
          axios.get(`${API_URL}/api/appointments/departments/company/${companyId}`, {
            headers: lmAuthHeader(),
            params: { activeOnly: true },
          }),
        ]);
        const list = Array.isArray(aptRes.data) ? aptRes.data : [];
        setAppointments(list);
        setDepartments(Array.isArray(deptRes.data) ? deptRes.data : []);
        setPhase(list.length > 0 ? "history" : "form");
      } catch (err) {
        console.error("Failed to load appointment data:", err);
        toast.error("Failed to load appointment data.");
        setPhase("form");
      }
    };
    load();
  }, [open, lead?.leadId, companyId]);

  useEffect(() => {
    if (!departmentId) {
      setPersons([]);
      setPersonId("");
      return;
    }
    setLoadingPersons(true);
    setPersonId("");
    axios
      .get(`${API_URL}/api/appointments/persons/company/${companyId}`, {
        headers: lmAuthHeader(),
        params: { activeOnly: true, departmentId },
      })
      .then((res) => setPersons(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error("Failed to load persons:", err);
        setPersons([]);
      })
      .finally(() => setLoadingPersons(false));
  }, [departmentId, companyId]);

  if (!open) return null;

  const handleSubmit = async () => {
    const dept = departments.find((d) => String(d.departmentId ?? d.id ?? d._id) === String(departmentId));
    const person = persons.find((p) => String(p.personId ?? p.id ?? p._id) === String(personId));
    if (!dept || !person || !preferredDate || !preferredTime) {
      toast.warning("Please select department, person, date and time.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/appointments`,
        {
          companyId,
          leadId: lead.leadId,
          leadName: lead.name,
          leadPhone: lead.phone,
          leadEmail: lead.email,
          preferredDate,
          preferredTime: preferredTime.length === 5 ? `${preferredTime}:00` : preferredTime,
          departmentId: dept.departmentId ?? dept.id ?? dept._id,
          departmentName: dept.name,
          departmentLocation: dept.location,
          personId: person.personId ?? person.id ?? person._id,
          personName: person.name,
          personPosition: person.position,
          status,
          notes,
          createdById: currentUserId,
          lastUpdatedById: currentUserId,
        },
        { headers: lmAuthHeader() }
      );
      toast.success("Appointment scheduled!");
      onScheduled?.();
      onClose();
    } catch (err) {
      console.error("Failed to save appointment:", err);
      toast.error(err.response?.data?.message || "Failed to save appointment.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full border rounded p-2 text-sm mt-1 dark:bg-gray-800 dark:text-white dark:border-gray-700";

  return (
    <div className="fixed inset-0 flex justify-center items-center z-[65] bg-black bg-opacity-40 lm-overlay-in">
      <div className="bg-white p-6 rounded-lg shadow-lg lm-modal-pop w-full max-w-lg max-h-[85vh] overflow-y-auto lm-scroll overscroll-contain dark:bg-gray-900">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold cfg-heading dark:text-white">
            {phase === "loading" && "Loading Appointments"}
            {phase === "history" && "Appointment History"}
            {phase === "form" && "Schedule Appointment"}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {phase === "loading" && (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-t-4 border-b-4 border-[#673AB7]"></div>
          </div>
        )}

        {phase === "history" && (
          <>
            <button
              onClick={() => setPhase("form")}
              className="mb-3 px-4 py-2 bg-[#673AB7] text-white text-sm rounded hover:bg-[#5E35A6]"
            >
              + Make New Appointment
            </button>
            <div className="space-y-2">
              {appointments.map((apt) => (
                <div key={apt.appointmentId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
                  <div className="flex justify-between items-start mb-1 gap-2">
                    <span className="text-sm font-semibold text-gray-800 dark:text-white">{apt.departmentName}</span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EDE9FE] text-[#673AB7] uppercase shrink-0">{apt.status}</span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Person: {apt.personName}{apt.personPosition ? ` (${apt.personPosition})` : ""}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    📅 {apt.preferredDate} 🕒 {apt.preferredTime ? apt.preferredTime.substring(0, 5) : ""}
                  </p>
                  {apt.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Notes: {apt.notes}</p>}
                </div>
              ))}
            </div>
          </>
        )}

        {phase === "form" && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Department</label>
              <select className={inputCls} value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                <option value="">— select department —</option>
                {departments.map((d) => {
                  const id = d.departmentId ?? d.id ?? d._id;
                  return <option key={id} value={id}>{d.name}{d.location ? ` · ${d.location}` : ""}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Person</label>
              <select
                className={inputCls}
                value={personId}
                onChange={(e) => setPersonId(e.target.value)}
                disabled={!departmentId || loadingPersons}
              >
                <option value="">{loadingPersons ? "Loading…" : "— select person —"}</option>
                {persons.map((p) => {
                  const id = p.personId ?? p.id ?? p._id;
                  return <option key={id} value={id}>{p.name}{p.position ? ` · ${p.position}` : ""}</option>;
                })}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Date</label>
                <input
                  type="date"
                  className={inputCls}
                  value={preferredDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setPreferredDate(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Time</label>
                <input type="time" className={inputCls} value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</label>
              <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)}>
                {LM_APT_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Notes</label>
              <textarea className={inputCls + " h-20"} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Enter notes…" />
            </div>
          </div>
        )}

        <div className="flex justify-end mt-4 gap-2">
          {phase === "history" && (
            <button onClick={onClose} className="px-4 py-2 bg-[#F3F4F6] text-[#374151] rounded hover:bg-[#E5E7EB] text-sm">Close</button>
          )}
          {phase === "form" && (
            <>
              {appointments.length > 0 && (
                <button onClick={() => setPhase("history")} disabled={submitting} className="px-4 py-2 bg-[#F3F4F6] text-[#374151] rounded hover:bg-[#E5E7EB] text-sm">Back</button>
              )}
              <button onClick={onClose} disabled={submitting} className="px-4 py-2 bg-[#F3F4F6] text-[#374151] rounded hover:bg-[#E5E7EB] text-sm">Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 py-2 bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] text-sm disabled:opacity-50"
              >
                {submitting ? "Scheduling…" : "Schedule"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}